import * as THREE from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { FikoSahne } from './FikoSahne';

/**
 * 🎯 FikoSmartPoint3D - CSS3DRenderer ile Akıllı Bağlantı Noktası
 *
 * CSS3DObject kullanarak doğru derinlik sıralaması sağlar.
 * Sabit boyut desteği ve CSS2D'deki gibi tıklama ile ikon/info box toggle.
 */

export type SmartPoint3DCategory =
    | 'info' | 'warning' | 'error' | 'success' | 'measure' | 'reference' | 'custom';

// Leader line tipi
export type LeaderTip = 'none' | 'straight' | 'bent';

// GD&T (Geometric Dimensioning & Tolerancing) tipleri
export type GDTTip =
    | 'flatness'        // ⏥ Düzlük
    | 'straightness'    // ⏤ Doğrusallık
    | 'circularity'     // ○ Dairesellik
    | 'cylindricity'    // ⌭ Silindiriklik
    | 'parallelism'     // ∥ Paralellik
    | 'perpendicularity'// ⊥ Diklik
    | 'angularity'      // ∠ Açısallık
    | 'position'        // ⌖ Konum
    | 'concentricity'   // ◎ Eşmerkezlilik
    | 'symmetry'        // ≡ Simetri
    | 'runout'          // ↗ Salınım
    | 'totalRunout'     // ↗↗ Toplam salınım
    | 'profileLine'     // ⌒ Profil (çizgi)
    | 'profileSurface'; // ⌓ Profil (yüzey)

// Yüzey kalitesi tipleri
export type YuzeyKaliteTip =
    | 'machined'   // İşlenmiş
    | 'ground'     // Taşlanmış
    | 'lapped'     // Alıştırılmış
    | 'polished'   // Parlatılmış
    | 'cast'       // Döküm
    | 'forged';    // Dövme

// Kaynak tipi
export type KaynakTip =
    | 'fillet'     // Köşe kaynağı
    | 'groove'     // Oluk kaynağı
    | 'plug'       // Tıpa kaynağı
    | 'spot'       // Nokta kaynağı
    | 'seam'       // Dikiş kaynağı
    | 'surfacing'; // Yüzey kaynağı

// ═══════════════════════════════════════════════════════════════════════════
// ORTAK: POZİSYON VE YÖN AYARLARI
// ═══════════════════════════════════════════════════════════════════════════

// Pozisyon offset (her PRO element için)
export interface IPozisyonOffset {
    x: number;  // X offset (birim: 3D dünya)
    y: number;  // Y offset
    z: number;  // Z offset
}

// Düzlem seçimi (oklar ve çizgiler için)
export type DuzlemTip = 'XY' | 'XZ' | 'YZ' | 'auto' | 'screen';

// Yön seçimi
export type YonTip = 'sol' | 'sag' | 'ust' | 'alt' | 'solUst' | 'sagUst' | 'solAlt' | 'sagAlt' | 'auto';

// ═══════════════════════════════════════════════════════════════════════════
// YENİ: DATUM MARKER TİPLERİ
// ═══════════════════════════════════════════════════════════════════════════
export type DatumTip = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'custom';

// Datum Marker ayarları
export interface IDatumConfig {
    aktif: boolean;
    tip?: DatumTip;           // A, B, C...
    customLabel?: string;     // Custom label için
    renk?: string;            // Kutu rengi
    boyut?: number;           // Kutu boyutu
    ucgenGoster?: boolean;    // Üçgen göster (hedef datum için)
    seffafArkaplan?: boolean; // Şeffaf arka plan
    // Pozisyon kontrolü
    offset?: IPozisyonOffset; // X, Y, Z offset
    yon?: YonTip;             // Yerleşim yönü
    duzlem?: DuzlemTip;       // 3D düzlem seçimi (XY, XZ, YZ)
}

// ═══════════════════════════════════════════════════════════════════════════
// YENİ: DİMENSİON LİNE (ÖLÇÜ ÇİZGİSİ) TİPLERİ
// ═══════════════════════════════════════════════════════════════════════════
export type DimensionTip = 'linear' | 'diameter' | 'radius' | 'angle';

// Dimension Line ayarları
export interface IDimensionConfig {
    aktif: boolean;
    tip?: DimensionTip;       // linear, diameter, radius, angle
    deger?: string;           // Ölçü değeri (örn: "4.489")
    toleransUst?: string;     // Üst tolerans (örn: "+.010")
    toleransAlt?: string;     // Alt tolerans (örn: "-.010")
    birim?: string;           // Birim (mm, in, °)
    extensionUzunluk?: number; // Extension line uzunluğu
    okTip?: 'arrow' | 'dot' | 'slash' | 'none'; // Ok tipi
    renk?: string;
    yaziRenk?: string;
    yaziBoyut?: number;
    seffafArkaplan?: boolean; // Şeffaf arka plan
    // Pozisyon ve geometri kontrolü
    offset?: IPozisyonOffset; // X, Y, Z offset
    duzlem?: DuzlemTip;       // Çizim düzlemi (XY, XZ, YZ)
    uzunluk?: number;         // Ölçü çizgisi uzunluğu
    // Başlangıç ve bitiş noktaları (tamamen özelleştirilebilir)
    baslangicNokta?: { x: number; y: number; z: number };  // Sol nokta
    bitisNokta?: { x: number; y: number; z: number };      // Sağ nokta
    // Extension line ayrıntılı kontrol
    extSolUzunluk?: number;   // Sol extension uzunluğu
    extSagUzunluk?: number;   // Sağ extension uzunluğu
    extOffset?: number;       // Extension gap (çizgiden uzaklık)
}

// ═══════════════════════════════════════════════════════════════════════════
// GENİŞLETİLMİŞ: LEADER LİNE (EXTENSION LİNE DESTEĞİ)
// ═══════════════════════════════════════════════════════════════════════════
export type LeaderStil = 'solid' | 'dashed' | 'dotted';
export type OkStil = 'filled' | 'open' | 'dot' | 'none';

// Kamera konum verisi interface'i
export interface IKameraKonum {
    position: number[];      // Kamera pozisyonu [x, y, z]
    quaternion: number[];    // Kamera rotasyonu [x, y, z, w]
    hedef: number[];         // Kontrol hedefi (target) [x, y, z]
    ust: number[];           // Up vektörü [x, y, z]
    yakinlik: number;        // Zoom değeri
    oran: number;            // Aspect ratio
}

// Leader Line (Callout) ayarları - GENİŞLETİLMİŞ
export interface ILeaderConfig {
    aktif: boolean;
    tip?: LeaderTip;          // Opsiyonel - aktif=false olduğunda gerekmez
    anchorPoint?: number[];   // Geometriye bağlanan nokta [x, y, z]
    bendPoint?: number[];     // Kırılma noktası (bent için) [x, y, z]
    renk?: string;
    kalinlik?: number;
    okBoyut?: number;
    // Yeni: Extension line özellikleri
    extensionAktif?: boolean; // Extension line göster
    extensionUzunluk?: number; // Extension uzunluğu
    extensionOffset?: number;  // Geometriden uzaklık
    stil?: LeaderStil;        // Çizgi stili (solid, dashed, dotted)
    okStil?: OkStil;          // Ok stili (filled, open, dot, none)
    // YÖN VE POZİSYON KONTROLLERI
    yon?: YonTip;             // Çizgi yönü (8 yön + auto)
    duzlem?: DuzlemTip;       // Çizim düzlemi (XY, XZ, YZ, screen)
    offset?: IPozisyonOffset; // Başlangıç offset
    // Çizgi uzunluğu kontrolü
    uzunluk?: number;         // Leader uzunluğu (default: 5)
    bentUzunluk?: number;     // Bent kısmı uzunluğu
    bentAci?: number;         // Bent açısı (derece)
}

// GD&T ayarları
export interface IGDTConfig {
    aktif: boolean;
    tip?: GDTTip;             // Opsiyonel - aktif=false olduğunda gerekmez
    deger?: string;           // Tolerans değeri (örn: "0.05")
    datum?: string;           // Referans datum (örn: "A", "A-B")
    malzemeKosulu?: 'M' | 'L' | 'S';  // MMC, LMC, RFS
    seffafArkaplan?: boolean; // Şeffaf arka plan (endüstri standardı)
    // Pozisyon kontrolü
    offset?: IPozisyonOffset; // X, Y, Z offset
    yon?: YonTip;             // Yerleşim yönü
    duzlem?: DuzlemTip;       // 3D düzlem seçimi (XY, XZ, YZ)
}

// Yüzey Kalitesi ayarları
export interface IYuzeyKaliteConfig {
    aktif: boolean;
    tip?: YuzeyKaliteTip;     // Opsiyonel - aktif=false olduğunda gerekmez
    ra?: number;              // Ra pürüzlülük değeri (µm)
    rz?: number;              // Rz değeri (µm)
    islemYonu?: string;       // İşlem yönü sembolü
    seffafArkaplan?: boolean; // Şeffaf arka plan
    // Pozisyon kontrolü
    offset?: IPozisyonOffset; // X, Y, Z offset
    yon?: YonTip;             // Yerleşim yönü
    duzlem?: DuzlemTip;       // 3D düzlem seçimi (XY, XZ, YZ)
}

// Kaynak Sembolü ayarları
export interface IKaynakConfig {
    aktif: boolean;
    tip?: KaynakTip;          // Opsiyonel - aktif=false olduğunda gerekmez
    boyut?: string;           // Kaynak boyutu
    uzunluk?: string;         // Kaynak uzunluğu
    aralik?: string;          // Aralık (kesikli kaynak için)
    tamCevre?: boolean;       // Tam çevre kaynağı
    sahada?: boolean;         // Sahada kaynak
    seffafArkaplan?: boolean; // Şeffaf arka plan
    // Pozisyon kontrolü
    offset?: IPozisyonOffset; // X, Y, Z offset
    yon?: YonTip;             // Yerleşim yönü
    duzlem?: DuzlemTip;       // 3D düzlem seçimi (XY, XZ, YZ)
}

export interface FikoSmartPoint3DConfig {
    // ═══════════════════════════════════════════════════════════════
    // TEMEL AYARLAR
    // ═══════════════════════════════════════════════════════════════
    baslik: string;
    aciklama?: string;
    kategori?: SmartPoint3DCategory;
    renk?: string;
    ikon?: string;
    customIkon?: string;      // Kullanıcı tanımlı ikon (emoji, numara, yazı)
    ozelRenk?: string;        // Kullanıcı tanımlı nokta rengi (boş = kategori rengi)
    yaziRenk?: string;        // Kullanıcı tanımlı yazı rengi (boş = beyaz)
    boyut?: number;
    pencereBoyut?: number;    // Pencere (label) boyutu
    sabitBoyut?: boolean;
    etiketGorunsun?: boolean;
    animasyonAktif?: boolean;  // Halka animasyonu aktif mi
    kameraKonumu?: IKameraKonum;  // Kayıtlı kamera konumu
    metadata?: Record<string, any>;
    onEdit?: (point: FikoSmartPoint3D) => void;  // Sağ tık düzenleme callback

    // ═══════════════════════════════════════════════════════════════
    // PROFESYONEl CAD ÖZELLİKLERİ
    // ═══════════════════════════════════════════════════════════════

    // PRO-only mod - İkon gizlenir, sadece PRO badge'ler görünür
    proOnlyMod?: boolean;

    // Leader Line (Callout) - Geometriye çizgi ile bağlantı
    leader?: ILeaderConfig;

    // GD&T (Geometric Dimensioning & Tolerancing)
    gdt?: IGDTConfig;

    // Yüzey Kalitesi (Surface Finish)
    yuzeyKalite?: IYuzeyKaliteConfig;

    // Kaynak Sembolü (Weld Symbol)
    kaynak?: IKaynakConfig;

    // ═══════════════════════════════════════════════════════════════
    // YENİ PROFESYONEl CAD ÖZELLİKLERİ
    // ═══════════════════════════════════════════════════════════════

    // Datum Marker (A, B, C kutuları)
    datum?: IDatumConfig;

    // Dimension Line (Toleranslı ölçü çizgisi)
    dimension?: IDimensionConfig;
}

const KATEGORI_STILLERI: Record<SmartPoint3DCategory, { renk: string; ikon: string }> = {
    info: { renk: '#2196F3', ikon: 'ℹ️' },
    warning: { renk: '#FF9800', ikon: '⚠️' },
    error: { renk: '#F44336', ikon: '❌' },
    success: { renk: '#4CAF50', ikon: '✓' },
    measure: { renk: '#9C27B0', ikon: '📏' },
    reference: { renk: '#00BCD4', ikon: '📍' },
    custom: { renk: '#607D8B', ikon: '●' }
};

// ═══════════════════════════════════════════════════════════════════════════
// GD&T SEMBOLLERİ (ISO 1101 / ASME Y14.5)
// ═══════════════════════════════════════════════════════════════════════════
export const GDT_SEMBOLLER: Record<GDTTip, { sembol: string; ad: string; kategori: string }> = {
    flatness:        { sembol: '⏥', ad: 'Düzlük', kategori: 'Form' },
    straightness:    { sembol: '⏤', ad: 'Doğrusallık', kategori: 'Form' },
    circularity:     { sembol: '○', ad: 'Dairesellik', kategori: 'Form' },
    cylindricity:    { sembol: '⌭', ad: 'Silindiriklik', kategori: 'Form' },
    parallelism:     { sembol: '∥', ad: 'Paralellik', kategori: 'Yönelim' },
    perpendicularity:{ sembol: '⊥', ad: 'Diklik', kategori: 'Yönelim' },
    angularity:      { sembol: '∠', ad: 'Açısallık', kategori: 'Yönelim' },
    position:        { sembol: '⌖', ad: 'Konum', kategori: 'Konum' },
    concentricity:   { sembol: '◎', ad: 'Eşmerkezlilik', kategori: 'Konum' },
    symmetry:        { sembol: '≡', ad: 'Simetri', kategori: 'Konum' },
    runout:          { sembol: '↗', ad: 'Salınım', kategori: 'Salınım' },
    totalRunout:     { sembol: '⇗', ad: 'Toplam Salınım', kategori: 'Salınım' },
    profileLine:     { sembol: '⌒', ad: 'Profil (Çizgi)', kategori: 'Profil' },
    profileSurface:  { sembol: '⌓', ad: 'Profil (Yüzey)', kategori: 'Profil' }
};

// ═══════════════════════════════════════════════════════════════════════════
// YÜZEY KALİTESİ SEMBOLLERİ (ISO 1302)
// ═══════════════════════════════════════════════════════════════════════════
export const YUZEY_KALITE_SEMBOLLER: Record<YuzeyKaliteTip, { sembol: string; ad: string }> = {
    machined:  { sembol: '▽', ad: 'İşlenmiş' },
    ground:    { sembol: '▽▽', ad: 'Taşlanmış' },
    lapped:    { sembol: '▽▽▽', ad: 'Alıştırılmış' },
    polished:  { sembol: '◇', ad: 'Parlatılmış' },
    cast:      { sembol: '〰', ad: 'Döküm Yüzeyi' },
    forged:    { sembol: '⌇', ad: 'Dövme Yüzeyi' }
};

// ═══════════════════════════════════════════════════════════════════════════
// KAYNAK SEMBOLLERİ (ISO 2553 / AWS A2.4)
// ═══════════════════════════════════════════════════════════════════════════
export const KAYNAK_SEMBOLLER: Record<KaynakTip, { sembol: string; ad: string }> = {
    fillet:    { sembol: '△', ad: 'Köşe Kaynağı' },
    groove:    { sembol: 'V', ad: 'Oluk Kaynağı' },
    plug:      { sembol: '▢', ad: 'Tıpa Kaynağı' },
    spot:      { sembol: '●', ad: 'Nokta Kaynağı' },
    seam:      { sembol: '═', ad: 'Dikiş Kaynağı' },
    surfacing: { sembol: '▬', ad: 'Yüzey Kaynağı' }
};

// Varsayılan pozisyon offset
const DEFAULT_OFFSET: IPozisyonOffset = { x: 0, y: 0, z: 0 };

const DEFAULT_CONFIG: Required<FikoSmartPoint3DConfig> = {
    // Temel ayarlar
    baslik: 'Nokta',
    aciklama: '',
    kategori: 'info',
    renk: '#2196F3',
    ikon: 'ℹ️',
    customIkon: '',  // Boş = kategori ikonu kullanılır
    ozelRenk: '',    // Boş = kategori rengi kullanılır
    yaziRenk: '',    // Boş = beyaz kullanılır
    boyut: 48,  // Varsayılan boyut (12-240 arası)
    pencereBoyut: 100,  // Pencere boyutu (50-400 arası, 100 = referans)
    sabitBoyut: true,
    etiketGorunsun: false,
    animasyonAktif: true,  // Halka animasyonu varsayılan açık
    kameraKonumu: undefined as any,  // Kayıtlı kamera konumu
    metadata: {},
    onEdit: undefined as any,  // Sağ tık callback

    // Profesyonel CAD özellikleri - varsayılan kapalı
    proOnlyMod: false,  // PRO-only mod kapalı
    leader: {
        aktif: false,
        tip: 'straight',  // Varsayılan düz çizgi
        anchorPoint: undefined,
        bendPoint: undefined,
        renk: '#1A1A1A',
        kalinlik: 2,
        okBoyut: 8,
        // YENİ: Yön ve pozisyon
        yon: 'sol',              // Varsayılan sola
        duzlem: 'screen',        // Ekrana paralel
        offset: { ...DEFAULT_OFFSET },
        uzunluk: 5,
        bentUzunluk: 2,
        bentAci: 45
    },
    gdt: {
        aktif: false,
        tip: 'position',
        deger: '0.05',
        datum: '',
        malzemeKosulu: undefined,
        seffafArkaplan: true,    // YENİ: Varsayılan şeffaf
        offset: { x: 3, y: 1.5, z: 0 },  // SmartPoint'in sağ üstü
        yon: 'sagUst'
    },
    yuzeyKalite: {
        aktif: false,
        tip: 'machined',
        ra: 3.2,
        rz: undefined,
        islemYonu: '',
        seffafArkaplan: true,    // YENİ: Varsayılan şeffaf
        offset: { x: 2.5, y: -1.2, z: 0 },  // SmartPoint'in sağ altı
        yon: 'sagAlt'
    },
    kaynak: {
        aktif: false,
        tip: 'fillet',
        boyut: '',
        uzunluk: '',
        aralik: '',
        tamCevre: false,
        sahada: false,
        seffafArkaplan: true,    // YENİ: Varsayılan şeffaf
        offset: { x: -2.5, y: 1.2, z: 0 },  // SmartPoint'in sol üstü
        yon: 'solUst'
    },
    // Yeni PRO özellikler
    datum: {
        aktif: false,
        tip: 'A',
        customLabel: '',
        renk: '#1A1A1A',
        boyut: 24,
        ucgenGoster: true,
        seffafArkaplan: true,    // YENİ: Varsayılan şeffaf
        offset: { x: -4, y: 0, z: 0 },  // SmartPoint'in solunda
        yon: 'sol'
    },
    dimension: {
        aktif: false,
        tip: 'linear',
        deger: '',
        toleransUst: '',
        toleransAlt: '',
        birim: 'mm',
        extensionUzunluk: 2,
        okTip: 'arrow',
        renk: '#1A1A1A',
        yaziRenk: '#1A1A1A',
        yaziBoyut: 14,
        seffafArkaplan: true,    // YENİ: Varsayılan şeffaf
        offset: { x: 0, y: -3, z: 0 },  // SmartPoint'in altında
        duzlem: 'screen',
        uzunluk: 8,
        baslangicNokta: undefined as any,
        bitisNokta: undefined as any,
        extSolUzunluk: 2,
        extSagUzunluk: 2,
        extOffset: 0.3
    }
};

declare var TWEEN: any;

/**
 * Profesyonel Boyutlandırma Sabitleri
 * CSS3DRenderer için optimize edilmiş değerler
 *
 * SABİT BOYUT MODU:
 * - Zoom değişse de ekranda AYNI boyutta kalır
 * - Formül: finalScale = originalScale * (viewHeight / divisor) * factor
 *
 * DİNAMİK BOYUT MODU:
 * - 3D dünyada sabit boyut, zoom ile büyür/küçülür
 */
const SIZING_CONSTANTS = {
    // Sabit boyut modu - ekranda sabit piksel boyutu
    FIXED: {
        BASE_SCALE: 0.05,         // Başlangıç scale (daha büyük)
        VIEW_HEIGHT_DIVISOR: 200, // Daha küçük bölen = daha büyük sonuç
        SCALE_FACTOR: 1.0,        // Çarpan (1.0 = tam boyut)
        MIN_SCALE: 0.01,          // Minimum scale
        MAX_SCALE: 0.5            // Maximum scale
    },
    // Dinamik boyut modu - 3D dünya birimi bazlı
    DYNAMIC: {
        BASE_SCALE: 0.05          // Sabit 3D ölçek
    }
};

/**
 * FikoSmartPoint3D - CSS3DObject tabanlı 3D nokta (sabit boyut destekli)
 */
export class FikoSmartPoint3D extends THREE.Group {
    override type = 'FikoSmartPoint3D';

    private config: Required<FikoSmartPoint3DConfig>;

    // CSS3D Object (DOM element)
    private css3dObject: CSS3DObject | null = null;
    private containerElement: HTMLDivElement | null = null;
    private iconElement: HTMLDivElement | null = null;
    private labelElement: HTMLDivElement | null = null;

    // Profesyonel CAD elementleri (CSS3D - deprecated, ama fallback için tutulur)
    private leaderElement: HTMLDivElement | null = null;      // Leader line SVG container
    private gdtBadgeElement: HTMLDivElement | null = null;    // GD&T badge
    private yuzeyBadgeElement: HTMLDivElement | null = null;  // Surface finish badge
    private kaynakBadgeElement: HTMLDivElement | null = null; // Weld symbol badge

    // ═══════════════════════════════════════════════════════════════════════════
    // 3D PRO ELEMENTLER (THREE.js native - gerçek 3D geometri)
    // ═══════════════════════════════════════════════════════════════════════════
    private proGroup3D: THREE.Group | null = null;            // Tüm 3D PRO elementleri
    private leader3DLine: THREE.Line | null = null;           // 3D Leader çizgisi
    private leader3DArrow: THREE.Mesh | null = null;          // 3D Ok ucu
    private gdt3DMesh: THREE.Mesh | null = null;               // 3D GD&T badge (gerçek 3D Mesh)
    private yuzey3DMesh: THREE.Mesh | null = null;            // 3D Yüzey kalitesi (gerçek 3D Mesh)
    private kaynak3DMesh: THREE.Mesh | null = null;           // 3D Kaynak sembolü (gerçek 3D Mesh)
    private anchor3DSprite: THREE.Sprite | null = null;       // 3D Pro-only anchor noktası (sprite kalabilir)

    // 3D PRO textures (dispose için)
    private gdt3DTexture: THREE.CanvasTexture | null = null;
    private yuzey3DTexture: THREE.CanvasTexture | null = null;
    private kaynak3DTexture: THREE.CanvasTexture | null = null;
    private anchor3DTexture: THREE.CanvasTexture | null = null;

    // YENİ: Datum Marker 3D elementleri
    private datum3DMesh: THREE.Mesh | null = null;            // Gerçek 3D Mesh (sprite değil)
    private datum3DTexture: THREE.CanvasTexture | null = null;
    private datumTriangle3D: THREE.Mesh | null = null;
    private datumLine3D: THREE.Line | null = null;

    // YENİ: Dimension Line 3D elementleri
    private dimension3DGroup: THREE.Group | null = null;
    private dimensionLine3D: THREE.Line | null = null;
    private dimensionExtLines3D: THREE.Line[] = [];
    private dimensionArrows3D: THREE.Mesh[] = [];
    private dimensionText3DMesh: THREE.Mesh | null = null;    // Gerçek 3D Mesh (sprite değil)
    private dimensionText3DTexture: THREE.CanvasTexture | null = null;

    // Sahne
    public sahne: FikoSahne | null = null;
    public css3dSahne: THREE.Scene | null = null;

    // Ürün ağacı referansı (FikoEtiketler'e kayıt için)
    public fikoGrupRef: any = null;

    // Durum
    private isActive: boolean = false;

    // Kamera listener
    private kontrolDegistiEvent = () => this.kameraYonDegisti();
    private listenerEklendi: boolean = false;

    // İkon animasyonu
    private pulseAnimationId: number | null = null;
    private styleElement: HTMLStyleElement | null = null;

    // Sabit boyut için orijinal scale (FikoCSS3DAnnotation pattern)
    private originalScale: THREE.Vector3 = new THREE.Vector3(1, 1, 1);

    constructor(config?: Partial<FikoSmartPoint3DConfig>) {
        super();

        const kategori = config?.kategori || 'info';
        const kategoriStil = KATEGORI_STILLERI[kategori];

        this.config = {
            ...DEFAULT_CONFIG,
            ...config,
            renk: config?.renk || kategoriStil.renk,
            ikon: config?.ikon || kategoriStil.ikon
        };

        this.name = `SmartPoint3D_${this.config.baslik}`;
    }

    /**
     * Başlat
     */
    init(): void {
        this.createDOMElements();

        if (this.config.etiketGorunsun) {
            this.isActive = true;
            this.showLabel();
            if (this.iconElement) {
                this.iconElement.style.animation = 'none';
                this.iconElement.style.opacity = '0';
                this.iconElement.style.pointerEvents = 'none';
            }
        }
    }

    /**
     * DOM elementlerini oluştur
     */
    private createDOMElements(): void {
        // Container - tek CSS3DObject için
        this.containerElement = document.createElement('div');
        this.containerElement.className = 'fiko-smart-point-3d-container';
        this.containerElement.setAttribute('data-interactive', 'true');

        Object.assign(this.containerElement.style, {
            position: 'relative',
            pointerEvents: 'auto',
            transform: 'translate(-50%, -50%)',
            overflow: 'visible'  // PRO badge'lerin görünmesi için
        });

        // PRO-only modda sadece minimal anchor noktası, aksi halde tam ikon
        if (this.config.proOnlyMod) {
            this.createProOnlyAnchor();
        } else {
            // İkon elementi
            this.createIcon();

            // Label elementi
            this.createLabel();
        }

        // ═══════════════════════════════════════════════════════════════
        // NOT: PRO CAD ELEMENTLERİ (Leader, GD&T, Yüzey, Kaynak)
        // artık gerçek 3D geometry olarak hazirla() metodunda oluşturuluyor
        // CSS3D DOM elementleri yerine THREE.Line ve THREE.Sprite kullanılıyor
        // ═══════════════════════════════════════════════════════════════

        // CSS3DObject oluştur
        this.css3dObject = new CSS3DObject(this.containerElement);

        // FikoCSS3DAnnotation ile AYNI: originalScale = 0.02 (sabit)
        // Boyut slider değeri guncelleScale içinde uygulanacak
        const baseScale = SIZING_CONSTANTS.FIXED.BASE_SCALE; // 0.02
        this.originalScale.set(baseScale, baseScale, baseScale);
        this.css3dObject.scale.copy(this.originalScale);
    }

    /**
     * Boyut çarpanını hesapla (12-120 aralığı için)
     * 32 = referans boyut (1.0x)
     */
    private getBoyutCarpani(): number {
        return this.config.boyut / 32;
    }

    /**
     * Pencere boyut çarpanını hesapla (50-200 aralığı için)
     * 100 = referans boyut (1.0x)
     */
    private getPencereBoyutCarpani(): number {
        return this.config.pencereBoyut / 100;
    }

    /**
     * Aktif nokta rengini al (özel renk varsa onu, yoksa kategori rengi)
     */
    private getAktifRenk(): string {
        return this.config.ozelRenk?.trim() || this.config.renk;
    }

    /**
     * Aktif yazı rengini al (özel varsa onu, yoksa beyaz)
     */
    private getYaziRenk(): string {
        return this.config.yaziRenk?.trim() || '#FFFFFF';
    }

    /**
     * İkon oluştur - CSS2D benzeri halka (ring) animasyonlu tasarım
     * Merkez nokta + genişleyen halkalar
     */
    private createIcon(): void {
        // Önce CSS animasyon stillerini oluştur
        this.createAnimationStyles();

        this.iconElement = document.createElement('div');
        this.iconElement.className = 'fiko-smart-point-3d-icon';
        this.iconElement.setAttribute('data-interactive', 'true');

        // CSS3D için büyük piksel boyutu (scale ile küçülecek)
        const boyutCarpani = this.getBoyutCarpani();
        const domSize = Math.round(100 * boyutCarpani);

        Object.assign(this.iconElement.style, {
            width: `${domSize}px`,
            height: `${domSize}px`,
            position: 'relative',
            cursor: 'pointer',
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Animasyonlar
            transition: 'opacity 0.25s ease-out, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
            transform: 'scale(1)',
            userSelect: 'none'
        });

        // Merkez ikon (kategori ikonu)
        const centerIcon = document.createElement('div');
        centerIcon.className = 'fiko-sp3d-center';
        const iconSize = Math.round(domSize * 0.4);
        const aktifRenk = this.getAktifRenk();
        Object.assign(centerIcon.style, {
            position: 'absolute',
            width: `${iconSize}px`,
            height: `${iconSize}px`,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${aktifRenk}ee 0%, ${aktifRenk}99 100%)`,
            boxShadow: `0 0 ${iconSize * 0.4}px ${aktifRenk}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: `${iconSize * 0.55}px`,
            zIndex: '10',
            textShadow: '0 0 4px rgba(0,0,0,0.5)'
        });
        // Custom ikon varsa onu kullan, yoksa kategori ikonu
        const ikonIcerik = this.config.customIkon?.trim() || this.config.ikon;
        centerIcon.textContent = ikonIcerik;
        this.iconElement.appendChild(centerIcon);

        // Halka animasyonları (animasyon aktifse)
        if (this.config.animasyonAktif) {
            // 3 adet genişleyen halka
            for (let i = 0; i < 3; i++) {
                const ring = document.createElement('div');
                ring.className = 'fiko-sp3d-ring';
                const ringSize = domSize;
                const borderWidth = Math.max(2, Math.round(domSize * 0.03));

                Object.assign(ring.style, {
                    position: 'absolute',
                    width: `${ringSize}px`,
                    height: `${ringSize}px`,
                    borderRadius: '50%',
                    border: `${borderWidth}px solid ${aktifRenk}`,
                    background: 'transparent',
                    opacity: '0',
                    animation: `fikoSmartPointRing 2.5s ease-out infinite`,
                    animationDelay: `${i * 0.8}s`,
                    pointerEvents: 'none'
                });
                this.iconElement.appendChild(ring);
            }
        } else {
            // Animasyon kapalıysa sadece statik halka
            const staticRing = document.createElement('div');
            const ringSize = Math.round(domSize * 0.7);
            const borderWidth = Math.max(2, Math.round(domSize * 0.04));
            Object.assign(staticRing.style, {
                position: 'absolute',
                width: `${ringSize}px`,
                height: `${ringSize}px`,
                borderRadius: '50%',
                border: `${borderWidth}px solid ${aktifRenk}`,
                background: 'transparent',
                opacity: '0.6'
            });
            this.iconElement.appendChild(staticRing);
        }

        // Hover efekti
        this.iconElement.addEventListener('mouseenter', () => {
            if (this.iconElement && !this.isActive) {
                this.iconElement.style.transform = 'scale(1.1)';
                const center = this.iconElement.querySelector('.fiko-sp3d-center') as HTMLElement;
                if (center) {
                    center.style.boxShadow = `0 0 ${iconSize * 0.6}px ${aktifRenk}`;
                }
            }
        });

        this.iconElement.addEventListener('mouseleave', () => {
            if (this.iconElement && !this.isActive) {
                this.iconElement.style.transform = 'scale(1)';
                const center = this.iconElement.querySelector('.fiko-sp3d-center') as HTMLElement;
                if (center) {
                    center.style.boxShadow = `0 0 ${iconSize * 0.4}px ${aktifRenk}`;
                }
            }
        });

        // Tıklama
        this.iconElement.addEventListener('click', (e) => {
            e.stopPropagation();

            // Ctrl+Tıklama: Kayıtlı kamera konumuna git, animasyon bitince pencereyi aç
            if (e.ctrlKey && this.hasKameraKonumu()) {
                this.kameraKonumunaGit(750).then(() => {
                    // Rotasyon tamamlandıktan sonra pencereyi aç (eğer kapalıysa)
                    // Zoom arka planda devam eder (sürekli çizim aktif)
                    if (!this.isActive) {
                        this.isActive = true;
                        if (this.iconElement) {
                            this.iconElement.style.animation = 'none';
                            this.iconElement.style.transform = 'scale(0.3)';
                            this.iconElement.style.opacity = '0';
                            this.iconElement.style.pointerEvents = 'none';
                            this.iconElement.style.filter = 'blur(4px)';
                        }
                        this.showLabel();
                    }
                });
                return;
            }

            if (this.iconElement) {
                this.iconElement.style.transform = 'scale(0.9)';
                setTimeout(() => {
                    this.toggleActive();
                }, 80);
            }
        });

        // Sağ tıklama - Düzenleme dialoğu
        this.iconElement.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.onContextMenu();
        });

        this.containerElement?.appendChild(this.iconElement);
    }

    /**
     * CSS animasyon stillerini oluştur - Halka (ring) animasyonu
     */
    private createAnimationStyles(): void {
        // Aynı stil birden fazla eklemesin
        if (document.getElementById('fiko-smart-point-animations')) return;

        this.styleElement = document.createElement('style');
        this.styleElement.id = 'fiko-smart-point-animations';
        this.styleElement.textContent = `
            @keyframes fikoSmartPointRing {
                0% {
                    transform: scale(0.3);
                    opacity: 0.8;
                }
                100% {
                    transform: scale(1.2);
                    opacity: 0;
                }
            }

            .fiko-smart-point-3d-icon:active {
                transform: scale(0.85) !important;
            }
        `;
        document.head.appendChild(this.styleElement);
    }

    /**
     * Label oluştur - Ayrı boyut ayarı ile
     * CSS3DRenderer için DOM elementi - pencereBoyut ile kontrol edilir
     */
    private createLabel(): void {
        this.labelElement = document.createElement('div');
        this.labelElement.className = 'fiko-smart-point-3d-label';
        this.labelElement.setAttribute('data-interactive', 'true');

        // Pencere boyutu ayrı ayarlanabilir (100 = referans = 100px base)
        const pencereCarpani = this.getPencereBoyutCarpani();
        const domSize = Math.round(100 * pencereCarpani);

        // Font boyutları - pencere boyutuyla orantılı
        const baseFontSize = Math.round(domSize * 0.16);  // ~16px at 100px
        const descFontSize = Math.round(domSize * 0.12);  // ~12px at 100px
        const paddingV = Math.round(domSize * 0.10);      // ~10px at 100px
        const paddingH = Math.round(domSize * 0.15);      // ~15px at 100px
        const borderWidth = Math.max(2, Math.round(domSize * 0.03));
        const borderRadius = Math.round(domSize * 0.05);

        const aktifRenk = this.getAktifRenk();
        const yaziRenk = this.getYaziRenk();

        Object.assign(this.labelElement.style, {
            position: 'absolute',
            // İkonun merkezinde açılsın (container'ın merkezine göre)
            top: '50%',
            left: '50%',
            // Ultra-thin: Şeffaf arka plan
            background: 'rgba(15, 15, 15, 0.9)',
            backdropFilter: 'blur(10px)',
            webkitBackdropFilter: 'blur(10px)',
            color: yaziRenk,
            padding: `${paddingV}px ${paddingH}px`,
            borderRadius: `${borderRadius}px`,
            fontSize: `${baseFontSize}px`,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontWeight: '300',
            letterSpacing: '0.3px',
            pointerEvents: 'none',
            cursor: 'pointer',
            userSelect: 'none',
            borderLeft: `${borderWidth}px solid ${aktifRenk}`,
            boxShadow: `0 4px 20px rgba(0,0,0,0.4)`,
            whiteSpace: 'nowrap',
            display: 'block',
            opacity: '0',
            // Animasyon - scale + opacity
            transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
            transform: 'translate(-50%, -50%) scale(0.8)',
            transformOrigin: 'center center',
            minWidth: `${Math.round(domSize * 1.2)}px`
        });

        // İçerik - orantılı tipografi, başlık rengi = aktif renk
        const marginBottom = this.config.aciklama ? Math.round(domSize * 0.06) : 0;
        let content = `<div style="color:${aktifRenk};font-size:${baseFontSize}px;font-weight:500;margin-bottom:${marginBottom}px">${this.config.baslik}</div>`;
        if (this.config.aciklama) {
            // Açıklama yazı rengini kullan, opacity ile
            const descColor = yaziRenk === '#FFFFFF' ? 'rgba(255,255,255,0.65)' : yaziRenk;
            content += `<div style="color:${descColor};font-size:${descFontSize}px;font-weight:300;line-height:1.3">${this.config.aciklama}</div>`;
        }
        this.labelElement.innerHTML = content;

        this.labelElement.addEventListener('click', (e) => {
            e.stopPropagation();

            // Ctrl+Tıklama: Kayıtlı kamera konumuna git
            if (e.ctrlKey && this.hasKameraKonumu()) {
                this.kameraKonumunaGit(750);
                return;
            }

            this.toggleActive();
        });

        // Sağ tıklama - Düzenleme dialoğu
        this.labelElement.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.onContextMenu();
        });

        this.containerElement?.appendChild(this.labelElement);
    }

    /**
     * PRO-only mod için minimal anchor noktası oluştur
     * İkon yerine sadece küçük bir işaretleyici
     */
    private createProOnlyAnchor(): void {
        const boyutCarpani = this.getBoyutCarpani();
        const anchorSize = Math.round(16 * boyutCarpani);
        const aktifRenk = this.config.ozelRenk || this.config.renk;

        // Minimal anchor element
        this.iconElement = document.createElement('div');
        this.iconElement.className = 'fiko-sp3d-pro-anchor';

        Object.assign(this.iconElement.style, {
            width: `${anchorSize}px`,
            height: `${anchorSize}px`,
            borderRadius: '50%',
            background: aktifRenk,
            border: '2px solid #FFFFFF',
            boxShadow: `0 0 6px ${aktifRenk}, 0 2px 4px rgba(0,0,0,0.3)`,
            cursor: 'pointer',
            transition: 'transform 0.2s ease',
            position: 'relative'
        });

        // Hover efekti
        this.iconElement.addEventListener('mouseenter', () => {
            if (this.iconElement) {
                this.iconElement.style.transform = 'scale(1.3)';
            }
        });

        this.iconElement.addEventListener('mouseleave', () => {
            if (this.iconElement) {
                this.iconElement.style.transform = 'scale(1)';
            }
        });

        // Sağ tıklama - Düzenleme
        this.iconElement.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.onContextMenu();
        });

        this.containerElement?.appendChild(this.iconElement);
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // DEPRECATED: CSS3D PRO ELEMENTLER
    // Bu metodlar artık kullanılmıyor. Gerçek 3D geometry versiyonları
    // (createLeaderLine3D, createGDTBadge3D, vb.) kullanılmaktadır.
    // Fallback için korunmuştur.
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * @deprecated 3D versiyon için createLeaderLine3D() kullanın
     * Leader Line oluştur - CSS tabanlı çizgi + ok
     */
    private createLeaderLine(): void {
        if (!this.config.leader?.aktif) return;

        const leader = this.config.leader;
        const boyutCarpani = this.getBoyutCarpani();
        const domSize = Math.round(100 * boyutCarpani);

        this.leaderElement = document.createElement('div');
        this.leaderElement.className = 'fiko-sp3d-leader';

        const leaderColor = leader.renk || '#1A1A1A';
        const lineWidth = Math.max(2, leader.kalinlik || 2);
        const arrowSize = Math.max(8, leader.okBoyut || 10);
        // Çizgi uzunluğu boyuta orantılı - minimum 60px
        const lineLength = Math.max(60, domSize * 0.8);

        const isBent = leader.tip === 'bent';

        // Leader container - noktanın soluna pozisyonla
        Object.assign(this.leaderElement.style, {
            position: 'absolute',
            top: '50%',
            right: `${domSize * 0.5 + 5}px`,
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: isBent ? 'column' : 'row',
            alignItems: 'center',
            pointerEvents: 'none',
            zIndex: '100'
        });

        if (isBent) {
            // Kırık çizgi: yukarı dikey + sağa yatay
            const verticalLine = document.createElement('div');
            Object.assign(verticalLine.style, {
                width: `${lineWidth}px`,
                height: `${lineLength * 0.6}px`,
                background: leaderColor,
                borderRadius: '1px'
            });
            this.leaderElement.appendChild(verticalLine);

            const horizontalLine = document.createElement('div');
            Object.assign(horizontalLine.style, {
                width: `${lineLength * 0.4}px`,
                height: `${lineWidth}px`,
                background: leaderColor,
                borderRadius: '1px',
                marginTop: `-${lineWidth}px`,
                marginLeft: `${lineWidth}px`
            });
            this.leaderElement.appendChild(horizontalLine);
        } else {
            // Düz çizgi: yatay
            const line = document.createElement('div');
            Object.assign(line.style, {
                width: `${lineLength}px`,
                height: `${lineWidth}px`,
                background: leaderColor,
                borderRadius: '1px'
            });
            this.leaderElement.appendChild(line);

            // Ok ucu (sağa bakan) - çizginin ucunda
            const arrow = document.createElement('div');
            Object.assign(arrow.style, {
                width: '0',
                height: '0',
                marginLeft: '-2px',
                borderLeft: `${arrowSize}px solid ${leaderColor}`,
                borderTop: `${arrowSize / 2}px solid transparent`,
                borderBottom: `${arrowSize / 2}px solid transparent`
            });
            this.leaderElement.appendChild(arrow);
        }

        this.containerElement?.appendChild(this.leaderElement);
    }

    /**
     * @deprecated 3D versiyon için createGDTBadge3D() kullanın
     * GD&T Badge oluştur - ISO 1101 / ASME Y14.5 standardına uygun
     */
    private createGDTBadge(): void {
        if (!this.config.gdt?.aktif) return;

        const gdt = this.config.gdt;
        const gdtInfo = GDT_SEMBOLLER[gdt.tip];
        const boyutCarpani = this.getBoyutCarpani();
        const badgeSize = Math.round(60 * boyutCarpani);

        this.gdtBadgeElement = document.createElement('div');
        this.gdtBadgeElement.className = 'fiko-sp3d-gdt-badge';

        Object.assign(this.gdtBadgeElement.style, {
            position: 'absolute',
            top: `${-badgeSize * 0.8}px`,
            right: `${-badgeSize * 1.2}px`,
            display: 'flex',
            alignItems: 'center',
            background: '#FFFFFF',
            border: '2px solid #1A1A1A',
            borderRadius: '2px',
            padding: '2px 4px',
            fontFamily: 'Arial, sans-serif',
            fontSize: `${badgeSize * 0.25}px`,
            fontWeight: 'bold',
            color: '#1A1A1A',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
        });

        // GD&T Frame: [Sembol | Değer | Datum]
        // Sembol hücresi
        const symbolCell = document.createElement('span');
        symbolCell.textContent = gdtInfo.sembol;
        Object.assign(symbolCell.style, {
            padding: '0 4px',
            borderRight: '1px solid #1A1A1A',
            fontSize: `${badgeSize * 0.35}px`
        });
        this.gdtBadgeElement.appendChild(symbolCell);

        // Değer hücresi
        const valueCell = document.createElement('span');
        valueCell.textContent = gdt.deger;
        Object.assign(valueCell.style, {
            padding: '0 4px',
            borderRight: gdt.datum ? '1px solid #1A1A1A' : 'none'
        });
        // Malzeme koşulu
        if (gdt.malzemeKosulu) {
            const mc = document.createElement('sub');
            mc.textContent = gdt.malzemeKosulu === 'M' ? 'Ⓜ' : gdt.malzemeKosulu === 'L' ? 'Ⓛ' : 'Ⓢ';
            mc.style.fontSize = '0.7em';
            valueCell.appendChild(mc);
        }
        this.gdtBadgeElement.appendChild(valueCell);

        // Datum hücresi (varsa)
        if (gdt.datum) {
            const datumCell = document.createElement('span');
            datumCell.textContent = gdt.datum;
            Object.assign(datumCell.style, {
                padding: '0 4px',
                fontWeight: 'bold'
            });
            this.gdtBadgeElement.appendChild(datumCell);
        }

        this.containerElement?.appendChild(this.gdtBadgeElement);
    }

    /**
     * @deprecated 3D versiyon için createYuzeyBadge3D() kullanın
     * Yüzey Kalitesi Badge oluştur - ISO 1302 standardına uygun
     */
    private createYuzeyKaliteBadge(): void {
        if (!this.config.yuzeyKalite?.aktif) return;

        const yk = this.config.yuzeyKalite;
        const ykInfo = YUZEY_KALITE_SEMBOLLER[yk.tip];
        const boyutCarpani = this.getBoyutCarpani();
        const badgeSize = Math.round(50 * boyutCarpani);

        this.yuzeyBadgeElement = document.createElement('div');
        this.yuzeyBadgeElement.className = 'fiko-sp3d-yuzey-badge';

        Object.assign(this.yuzeyBadgeElement.style, {
            position: 'absolute',
            bottom: `${-badgeSize * 0.6}px`,
            right: `${-badgeSize * 1.0}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: '#FFFFFF',
            border: '1.5px solid #1A1A1A',
            borderRadius: '2px',
            padding: '2px 6px',
            fontFamily: 'Arial, sans-serif',
            fontSize: `${badgeSize * 0.3}px`,
            color: '#1A1A1A',
            pointerEvents: 'none',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
        });

        // Yüzey sembolü (üçgen)
        const symbol = document.createElement('div');
        symbol.textContent = ykInfo.sembol;
        symbol.style.fontSize = `${badgeSize * 0.4}px`;
        this.yuzeyBadgeElement.appendChild(symbol);

        // Ra değeri (varsa)
        if (yk.ra) {
            const raText = document.createElement('div');
            raText.textContent = `Ra ${yk.ra}`;
            raText.style.fontSize = `${badgeSize * 0.22}px`;
            this.yuzeyBadgeElement.appendChild(raText);
        }

        this.containerElement?.appendChild(this.yuzeyBadgeElement);
    }

    /**
     * @deprecated 3D versiyon için createKaynakBadge3D() kullanın
     * Kaynak Sembolü Badge oluştur - ISO 2553 / AWS A2.4 standardına uygun
     */
    private createKaynakBadge(): void {
        if (!this.config.kaynak?.aktif) return;

        const kaynak = this.config.kaynak;
        const kaynakInfo = KAYNAK_SEMBOLLER[kaynak.tip];
        const boyutCarpani = this.getBoyutCarpani();
        const badgeSize = Math.round(60 * boyutCarpani);

        this.kaynakBadgeElement = document.createElement('div');
        this.kaynakBadgeElement.className = 'fiko-sp3d-kaynak-badge';

        Object.assign(this.kaynakBadgeElement.style, {
            position: 'absolute',
            top: `${-badgeSize * 0.6}px`,
            left: `${-badgeSize * 1.2}px`,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            background: '#FFFFFF',
            border: '1.5px solid #1A1A1A',
            borderRadius: '2px',
            padding: '3px 6px',
            fontFamily: 'Arial, sans-serif',
            fontSize: `${badgeSize * 0.28}px`,
            color: '#1A1A1A',
            pointerEvents: 'none',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
        });

        // Kaynak sembolü
        const symbol = document.createElement('span');
        symbol.textContent = kaynakInfo.sembol;
        symbol.style.fontSize = `${badgeSize * 0.4}px`;
        this.kaynakBadgeElement.appendChild(symbol);

        // Boyut (varsa)
        if (kaynak.boyut) {
            const sizeText = document.createElement('span');
            sizeText.textContent = kaynak.boyut;
            this.kaynakBadgeElement.appendChild(sizeText);
        }

        // Tam çevre sembolü
        if (kaynak.tamCevre) {
            const allAround = document.createElement('span');
            allAround.textContent = '○';
            allAround.style.marginLeft = '2px';
            this.kaynakBadgeElement.appendChild(allAround);
        }

        // Sahada sembolü
        if (kaynak.sahada) {
            const field = document.createElement('span');
            field.textContent = '▸';
            field.style.marginLeft = '2px';
            this.kaynakBadgeElement.appendChild(field);
        }

        this.containerElement?.appendChild(this.kaynakBadgeElement);
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // 3D PRO ELEMENTLER - THREE.js NATIVE GEOMETRY
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * 3D PRO Group oluştur - Tüm 3D PRO elementlerini barındırır
     * Ana THREE.Scene'e eklenir (CSS3D sahnesine değil)
     */
    private create3DProGroup(): void {
        if (this.proGroup3D) {
            this.dispose3DProGroup();
        }

        this.proGroup3D = new THREE.Group();
        this.proGroup3D.name = `SmartPoint3D_PRO_${this.config.baslik}`;

        // Leader Line 3D
        if (this.config.leader?.aktif) {
            this.createLeaderLine3D();
        }

        // GD&T Badge 3D
        if (this.config.gdt?.aktif) {
            this.createGDTBadge3D();
        }

        // Yüzey Kalitesi Badge 3D
        if (this.config.yuzeyKalite?.aktif) {
            this.createYuzeyBadge3D();
        }

        // Kaynak Sembolü Badge 3D
        if (this.config.kaynak?.aktif) {
            this.createKaynakBadge3D();
        }

        // PRO-only modda 3D anchor
        if (this.config.proOnlyMod) {
            this.createAnchor3D();
        }

        // YENİ: Datum Marker 3D
        if (this.config.datum?.aktif) {
            this.createDatumMarker3D();
        }

        // YENİ: Dimension Line 3D
        if (this.config.dimension?.aktif) {
            this.createDimensionLine3D();
        }
    }

    /**
     * 3D Leader Line oluştur - THREE.Line ile gerçek 3D çizgi
     * TAM ÖZELLEŞTİRİLEBİLİR: 8 yön + düzlem seçimi + uzunluk kontrolü
     */
    private createLeaderLine3D(): void {
        if (!this.proGroup3D || !this.config.leader?.aktif) return;

        const leader = this.config.leader;
        const leaderColor = new THREE.Color(leader.renk || '#1A1A1A');
        const arrowSize = 0.4; // 3D dünya birimi

        // Çizgi uzunluğu - config'den veya varsayılan
        const lineLength = leader.uzunluk || 5;
        const bentLength = leader.bentUzunluk || lineLength * 0.4;

        // Yön vektörü hesapla (8 yön desteği)
        const yon = leader.yon || 'sol';
        const { dx, dy } = this.getYonVektoru(yon);

        // Offset varsa uygula
        const offsetX = leader.offset?.x || 0;
        const offsetY = leader.offset?.y || 0;
        const offsetZ = leader.offset?.z || 0;

        // Çizgi noktaları
        const points: THREE.Vector3[] = [];
        const startPoint = new THREE.Vector3(offsetX, offsetY, offsetZ);

        if (leader.tip === 'bent') {
            // Kırık çizgi - açı ile
            const bentAci = (leader.bentAci || 45) * Math.PI / 180;
            const bentDx = Math.cos(bentAci) * dx;
            const bentDy = Math.sin(bentAci) * (dy || 1);

            points.push(startPoint.clone());
            points.push(new THREE.Vector3(
                offsetX + bentDx * bentLength,
                offsetY + bentDy * bentLength,
                offsetZ
            ));
            points.push(new THREE.Vector3(
                offsetX + dx * lineLength,
                offsetY + bentDy * bentLength,
                offsetZ
            ));
        } else {
            // Düz çizgi - yöne göre
            points.push(startPoint.clone());
            points.push(new THREE.Vector3(
                offsetX + dx * lineLength,
                offsetY + dy * lineLength,
                offsetZ
            ));
        }

        // BufferGeometry oluştur
        const geometry = new THREE.BufferGeometry().setFromPoints(points);

        // Line material
        const material = new THREE.LineBasicMaterial({
            color: leaderColor,
            linewidth: 2,
            transparent: true,
            opacity: 1,
            depthTest: true,
            depthWrite: false
        });

        // Line oluştur
        this.leader3DLine = new THREE.Line(geometry, material);
        this.leader3DLine.name = 'LeaderLine3D';
        this.leader3DLine.renderOrder = 9997;
        this.leader3DLine.frustumCulled = false;

        this.proGroup3D.add(this.leader3DLine);

        // Ok ucu - yöne göre döndür
        const endPoint = points[points.length - 1];
        const arrowGeom = new THREE.ConeGeometry(arrowSize * 0.4, arrowSize, 8);

        // Ok yönünü hesapla
        const arrowRotation = this.getOkRotasyonu(yon);
        arrowGeom.rotateZ(arrowRotation);

        const arrowMat = new THREE.MeshBasicMaterial({
            color: leaderColor,
            transparent: true,
            opacity: 1,
            depthTest: true,
            depthWrite: false
        });

        this.leader3DArrow = new THREE.Mesh(arrowGeom, arrowMat);

        // Ok pozisyonu - çizgi ucunda
        this.leader3DArrow.position.set(
            endPoint.x + dx * arrowSize * 0.5,
            endPoint.y + dy * arrowSize * 0.5,
            endPoint.z
        );

        this.leader3DArrow.name = 'LeaderArrow3D';
        this.leader3DArrow.renderOrder = 9997;
        this.leader3DArrow.frustumCulled = false;

        this.proGroup3D.add(this.leader3DArrow);
    }

    /**
     * Yön tipinden vektör hesapla
     */
    private getYonVektoru(yon: YonTip): { dx: number; dy: number } {
        const yonlar: Record<YonTip, { dx: number; dy: number }> = {
            'sol': { dx: -1, dy: 0 },
            'sag': { dx: 1, dy: 0 },
            'ust': { dx: 0, dy: 1 },
            'alt': { dx: 0, dy: -1 },
            'solUst': { dx: -0.707, dy: 0.707 },
            'sagUst': { dx: 0.707, dy: 0.707 },
            'solAlt': { dx: -0.707, dy: -0.707 },
            'sagAlt': { dx: 0.707, dy: -0.707 },
            'auto': { dx: -1, dy: 0 }  // Varsayılan sol
        };
        return yonlar[yon] || yonlar['sol'];
    }

    /**
     * Yön tipinden ok rotasyonu hesapla
     */
    private getOkRotasyonu(yon: YonTip): number {
        const rotasyonlar: Record<YonTip, number> = {
            'sol': Math.PI / 2,        // Sola bakan
            'sag': -Math.PI / 2,       // Sağa bakan
            'ust': 0,                   // Yukarı bakan
            'alt': Math.PI,            // Aşağı bakan
            'solUst': Math.PI / 4,     // Sol üst
            'sagUst': -Math.PI / 4,    // Sağ üst
            'solAlt': 3 * Math.PI / 4, // Sol alt
            'sagAlt': -3 * Math.PI / 4,// Sağ alt
            'auto': Math.PI / 2        // Varsayılan sol
        };
        return rotasyonlar[yon] || rotasyonlar['sol'];
    }

    /**
     * 3D GD&T Badge oluştur - THREE.Sprite + Canvas texture
     * ENDÜSTRİ STANDARDI: ISO 1101 / ASME Y14.5 formatı
     */
    private createGDTBadge3D(): void {
        if (!this.proGroup3D || !this.config.gdt?.aktif) return;

        const gdt = this.config.gdt;
        const gdtInfo = GDT_SEMBOLLER[gdt.tip!];
        const seffaf = gdt.seffafArkaplan !== false; // Varsayılan şeffaf

        // Canvas oluştur
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        // Canvas boyutu (high DPI)
        const scale = 2;
        const width = 200;
        const height = 50;
        canvas.width = width * scale;
        canvas.height = height * scale;
        ctx.scale(scale, scale);

        // Arka plan - ŞEFFAF veya beyaz
        if (seffaf) {
            ctx.clearRect(0, 0, width, height);
        } else {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
        }

        // Helper: Şeffaf modda beyaz outline ile yazı çiz
        const drawTextWithOutline = (text: string, x: number, y: number) => {
            if (seffaf) {
                // Beyaz outline (daha kalın)
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 4;
                ctx.lineJoin = 'round';
                ctx.miterLimit = 2;
                ctx.strokeText(text, x, y);
            }
            // Siyah fill
            ctx.fillStyle = '#1A1A1A';
            ctx.fillText(text, x, y);
        };

        // Border - endüstri standardı kalın siyah çerçeve
        // Şeffaf modda da beyaz-siyah çerçeve
        if (seffaf) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 5;
            ctx.strokeRect(2, 2, width - 4, height - 4);
        }
        ctx.strokeStyle = '#1A1A1A';
        ctx.lineWidth = 2.5;
        ctx.strokeRect(2, 2, width - 4, height - 4);

        // Sembol hücresi - ISO formatı
        ctx.font = 'bold 26px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        drawTextWithOutline(gdtInfo.sembol, 30, height / 2);

        // Dikey ayırıcı çizgi
        if (seffaf) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.moveTo(55, 4);
            ctx.lineTo(55, height - 4);
            ctx.stroke();
        }
        ctx.strokeStyle = '#1A1A1A';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(55, 4);
        ctx.lineTo(55, height - 4);
        ctx.stroke();

        // Tolerans değeri
        ctx.font = 'bold 18px Arial';
        const valueX = gdt.datum ? 90 : 110;
        drawTextWithOutline(gdt.deger || '', valueX, height / 2);

        // Malzeme koşulu (MMC/LMC/RFS)
        if (gdt.malzemeKosulu) {
            ctx.font = 'bold 14px Arial';
            const mcSymbol = gdt.malzemeKosulu === 'M' ? 'Ⓜ' : gdt.malzemeKosulu === 'L' ? 'Ⓛ' : 'Ⓢ';
            drawTextWithOutline(mcSymbol, valueX + 40, height / 2);
        }

        // Datum referansı
        if (gdt.datum) {
            if (seffaf) {
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.moveTo(135, 4);
                ctx.lineTo(135, height - 4);
                ctx.stroke();
            }
            ctx.strokeStyle = '#1A1A1A';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(135, 4);
            ctx.lineTo(135, height - 4);
            ctx.stroke();

            ctx.font = 'bold 20px Arial';
            drawTextWithOutline(gdt.datum, 167, height / 2);
        }

        // Texture oluştur
        this.gdt3DTexture = new THREE.CanvasTexture(canvas);
        this.gdt3DTexture.minFilter = THREE.LinearFilter;
        this.gdt3DTexture.magFilter = THREE.LinearFilter;

        // ═══════════════════════════════════════════════════════════════════════════
        // GERÇEK 3D MESH - PlaneGeometry (Sprite DEĞİL!)
        // Canvas boyutu 200x50 = 4:1 aspect ratio
        // ═══════════════════════════════════════════════════════════════════════════
        const meshWidth = 4;
        const meshHeight = 1;
        const geometry = new THREE.PlaneGeometry(meshWidth, meshHeight);

        const material = new THREE.MeshBasicMaterial({
            map: this.gdt3DTexture,
            transparent: true,
            side: THREE.DoubleSide,  // Her iki taraftan görünür
            depthTest: true,
            depthWrite: false
        });

        // Mesh oluştur - gerçek 3D obje (billboard DEĞİL)
        this.gdt3DMesh = new THREE.Mesh(geometry, material);

        // POZİSYON: Config'den offset kullan
        const offset = gdt.offset || { x: 3, y: 1.5, z: 0 };
        this.gdt3DMesh.position.set(offset.x, offset.y, offset.z);

        // DÜZLEM ROTASYONU: Config'den düzlem seçimine göre
        const duzlem = gdt.duzlem || 'XY';
        this.applyDuzlemRotation(this.gdt3DMesh, duzlem);

        this.gdt3DMesh.name = 'GDTBadge3D';
        this.gdt3DMesh.renderOrder = 9999;
        this.gdt3DMesh.frustumCulled = false;

        this.proGroup3D.add(this.gdt3DMesh);
    }

    /**
     * Düzlem rotasyonunu uygula - 3D Mesh için
     * @param mesh THREE.Mesh objesi
     * @param duzlem Düzlem tipi (XY, XZ, YZ, auto, screen)
     */
    private applyDuzlemRotation(mesh: THREE.Mesh, duzlem: DuzlemTip): void {
        switch (duzlem) {
            case 'XY':
                // Varsayılan - XY düzleminde (Z'ye dik)
                mesh.rotation.set(0, 0, 0);
                break;
            case 'XZ':
                // XZ düzleminde (Y'ye dik) - yatay yüzey
                mesh.rotation.set(-Math.PI / 2, 0, 0);
                break;
            case 'YZ':
                // YZ düzleminde (X'e dik) - dikey yan yüzey
                mesh.rotation.set(0, Math.PI / 2, 0);
                break;
            case 'auto':
            case 'screen':
                // Varsayılan XY
                mesh.rotation.set(0, 0, 0);
                break;
        }
    }

    /**
     * 3D Yüzey Kalitesi Badge oluştur - THREE.Sprite + Canvas texture
     * ENDÜSTRİ STANDARDI: ISO 1302 formatı
     */
    private createYuzeyBadge3D(): void {
        if (!this.proGroup3D || !this.config.yuzeyKalite?.aktif) return;

        const yk = this.config.yuzeyKalite;
        const ykInfo = YUZEY_KALITE_SEMBOLLER[yk.tip!];
        const seffaf = yk.seffafArkaplan !== false; // Varsayılan şeffaf

        // Canvas oluştur
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        const scale = 2;
        const width = 80;
        const height = 60;
        canvas.width = width * scale;
        canvas.height = height * scale;
        ctx.scale(scale, scale);

        // Arka plan - ŞEFFAF veya beyaz
        if (seffaf) {
            ctx.clearRect(0, 0, width, height);
        } else {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
        }

        // Helper: Şeffaf modda beyaz outline ile yazı çiz
        const drawTextWithOutline = (text: string, x: number, y: number) => {
            if (seffaf) {
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 4;
                ctx.lineJoin = 'round';
                ctx.miterLimit = 2;
                ctx.strokeText(text, x, y);
            }
            ctx.fillStyle = '#1A1A1A';
            ctx.fillText(text, x, y);
        };

        // Border - endüstri standardı (şeffaf modda beyaz outline)
        if (seffaf) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 5;
            ctx.strokeRect(2, 2, width - 4, height - 4);
        }
        ctx.strokeStyle = '#1A1A1A';
        ctx.lineWidth = 2;
        ctx.strokeRect(2, 2, width - 4, height - 4);

        // Yüzey sembolü - ISO 1302
        ctx.font = 'bold 28px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        drawTextWithOutline(ykInfo.sembol, width / 2, height / 2 - 8);

        // Ra değeri
        if (yk.ra) {
            ctx.font = '12px Arial';
            drawTextWithOutline(`Ra ${yk.ra}`, width / 2, height / 2 + 18);
        }

        // Texture
        this.yuzey3DTexture = new THREE.CanvasTexture(canvas);
        this.yuzey3DTexture.minFilter = THREE.LinearFilter;
        this.yuzey3DTexture.magFilter = THREE.LinearFilter;

        // ═══════════════════════════════════════════════════════════════════════════
        // GERÇEK 3D MESH - PlaneGeometry (Sprite DEĞİL!)
        // Canvas boyutu 80x60 = 4:3 aspect ratio → 1.6:1.2
        // ═══════════════════════════════════════════════════════════════════════════
        const meshWidth = 1.6;
        const meshHeight = 1.2;
        const geometry = new THREE.PlaneGeometry(meshWidth, meshHeight);

        const material = new THREE.MeshBasicMaterial({
            map: this.yuzey3DTexture,
            transparent: true,
            side: THREE.DoubleSide,
            depthTest: true,
            depthWrite: false
        });

        this.yuzey3DMesh = new THREE.Mesh(geometry, material);

        // POZİSYON: Config'den offset kullan
        const offset = yk.offset || { x: 2.5, y: -1.2, z: 0 };
        this.yuzey3DMesh.position.set(offset.x, offset.y, offset.z);

        // DÜZLEM ROTASYONU
        const duzlem = yk.duzlem || 'XY';
        this.applyDuzlemRotation(this.yuzey3DMesh, duzlem);

        this.yuzey3DMesh.name = 'YuzeyBadge3D';
        this.yuzey3DMesh.renderOrder = 9999;
        this.yuzey3DMesh.frustumCulled = false;

        this.proGroup3D.add(this.yuzey3DMesh);
    }

    /**
     * 3D Kaynak Sembolü Badge oluştur - THREE.Sprite + Canvas texture
     * ENDÜSTRİ STANDARDI: ISO 2553 / AWS A2.4 formatı
     */
    private createKaynakBadge3D(): void {
        if (!this.proGroup3D || !this.config.kaynak?.aktif) return;

        const kaynak = this.config.kaynak;
        const kaynakInfo = KAYNAK_SEMBOLLER[kaynak.tip!];
        const seffaf = kaynak.seffafArkaplan !== false; // Varsayılan şeffaf

        // Canvas oluştur
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        const scale = 2;
        const width = 120;
        const height = 50;
        canvas.width = width * scale;
        canvas.height = height * scale;
        ctx.scale(scale, scale);

        // Arka plan - ŞEFFAF veya beyaz
        if (seffaf) {
            ctx.clearRect(0, 0, width, height);
        } else {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
        }

        // Helper: Şeffaf modda beyaz outline ile yazı çiz
        const drawTextWithOutline = (text: string, x: number, y: number) => {
            if (seffaf) {
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 4;
                ctx.lineJoin = 'round';
                ctx.miterLimit = 2;
                ctx.strokeText(text, x, y);
            }
            ctx.fillStyle = '#1A1A1A';
            ctx.fillText(text, x, y);
        };

        // Border - endüstri standardı (şeffaf modda beyaz outline)
        if (seffaf) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 5;
            ctx.strokeRect(2, 2, width - 4, height - 4);
        }
        ctx.strokeStyle = '#1A1A1A';
        ctx.lineWidth = 2;
        ctx.strokeRect(2, 2, width - 4, height - 4);

        // Kaynak sembolü
        ctx.font = '26px Arial';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        let xPos = 10;
        drawTextWithOutline(kaynakInfo.sembol, xPos, height / 2);
        xPos += 30;

        // Boyut
        if (kaynak.boyut) {
            ctx.font = '16px Arial';
            drawTextWithOutline(kaynak.boyut, xPos, height / 2);
            xPos += ctx.measureText(kaynak.boyut).width + 8;
        }

        // Tam çevre
        if (kaynak.tamCevre) {
            ctx.font = '18px Arial';
            drawTextWithOutline('○', xPos, height / 2);
            xPos += 20;
        }

        // Sahada
        if (kaynak.sahada) {
            ctx.font = '18px Arial';
            drawTextWithOutline('▸', xPos, height / 2);
        }

        // Texture
        this.kaynak3DTexture = new THREE.CanvasTexture(canvas);
        this.kaynak3DTexture.minFilter = THREE.LinearFilter;
        this.kaynak3DTexture.magFilter = THREE.LinearFilter;

        // ═══════════════════════════════════════════════════════════════════════════
        // GERÇEK 3D MESH - PlaneGeometry (Sprite DEĞİL!)
        // Canvas boyutu 120x50 = 2.4:1 aspect ratio
        // ═══════════════════════════════════════════════════════════════════════════
        const meshWidth = 2.4;
        const meshHeight = 1;
        const geometry = new THREE.PlaneGeometry(meshWidth, meshHeight);

        const material = new THREE.MeshBasicMaterial({
            map: this.kaynak3DTexture,
            transparent: true,
            side: THREE.DoubleSide,
            depthTest: true,
            depthWrite: false
        });

        this.kaynak3DMesh = new THREE.Mesh(geometry, material);

        // POZİSYON: Config'den offset kullan
        const offset = kaynak.offset || { x: -2.5, y: 1.2, z: 0 };
        this.kaynak3DMesh.position.set(offset.x, offset.y, offset.z);

        // DÜZLEM ROTASYONU
        const duzlem = kaynak.duzlem || 'XY';
        this.applyDuzlemRotation(this.kaynak3DMesh, duzlem);

        this.kaynak3DMesh.name = 'KaynakBadge3D';
        this.kaynak3DMesh.renderOrder = 9999;
        this.kaynak3DMesh.frustumCulled = false;

        this.proGroup3D.add(this.kaynak3DMesh);
    }

    /**
     * 3D Anchor noktası oluştur (PRO-only mod için)
     */
    private createAnchor3D(): void {
        if (!this.proGroup3D) return;

        const aktifRenk = this.config.ozelRenk || this.config.renk;

        // Canvas ile daire oluştur
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        const size = 64;
        canvas.width = size;
        canvas.height = size;

        // Daire çiz
        const radius = size / 2 - 4;
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, radius, 0, Math.PI * 2);
        ctx.fillStyle = aktifRenk;
        ctx.fill();

        // Beyaz border
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 3;
        ctx.stroke();

        // Glow efekti
        ctx.shadowColor = aktifRenk;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, radius - 2, 0, Math.PI * 2);
        ctx.stroke();

        // Texture
        this.anchor3DTexture = new THREE.CanvasTexture(canvas);
        this.anchor3DTexture.minFilter = THREE.LinearFilter;
        this.anchor3DTexture.magFilter = THREE.LinearFilter;

        // Sprite - depthTest kapalı
        const material = new THREE.SpriteMaterial({
            map: this.anchor3DTexture,
            transparent: true,
            depthTest: true,
            depthWrite: false,
            sizeAttenuation: false
        });

        this.anchor3DSprite = new THREE.Sprite(material);
        this.anchor3DSprite.scale.set(0.5, 0.5, 1); // Daha büyük anchor
        this.anchor3DSprite.position.set(0, 0, 0);
        this.anchor3DSprite.name = 'Anchor3D';
        this.anchor3DSprite.renderOrder = 9998;

        this.proGroup3D.add(this.anchor3DSprite);
    }

    /**
     * ═══════════════════════════════════════════════════════════════════════════
     * 3D DATUM MARKER - Profesyonel CAD datum kutusu (A, B, C...)
     * ENDÜSTRİ STANDARDI: ISO 5459 / ASME Y14.5 Datum formatı
     * ═══════════════════════════════════════════════════════════════════════════
     */
    private createDatumMarker3D(): void {
        if (!this.proGroup3D || !this.config.datum?.aktif) return;

        const datum = this.config.datum;
        const datumLabel = datum.tip === 'custom' ? (datum.customLabel || 'X') : datum.tip;
        const datumRenk = datum.renk || '#1A1A1A';
        const datumBoyut = datum.boyut || 24;
        const seffaf = datum.seffafArkaplan !== false; // Varsayılan şeffaf

        // Canvas ile datum kutusu oluştur
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        const scale = 2;
        const boxSize = datumBoyut * 2; // Kutu boyutu
        const totalHeight = datum.ucgenGoster ? boxSize * 1.6 : boxSize;

        canvas.width = boxSize * scale;
        canvas.height = totalHeight * scale;
        ctx.scale(scale, scale);

        // Temizle (şeffaf için)
        ctx.clearRect(0, 0, boxSize, totalHeight);

        // Helper: Şeffaf modda beyaz outline ile yazı çiz
        const drawTextWithOutline = (text: string, x: number, y: number) => {
            if (seffaf) {
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 5;
                ctx.lineJoin = 'round';
                ctx.miterLimit = 2;
                ctx.strokeText(text, x, y);
            }
            ctx.fillStyle = datumRenk;
            ctx.fillText(text, x, y);
        };

        // Üçgen (datum hedef göstergesi) - beyaz outline ile
        if (datum.ucgenGoster) {
            const triHeight = boxSize * 0.5;
            ctx.beginPath();
            ctx.moveTo(boxSize / 2, totalHeight); // Alt köşe
            ctx.lineTo(boxSize / 2 - triHeight * 0.6, totalHeight - triHeight); // Sol
            ctx.lineTo(boxSize / 2 + triHeight * 0.6, totalHeight - triHeight); // Sağ
            ctx.closePath();
            // Şeffaf modda beyaz outline
            if (seffaf) {
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 4;
                ctx.stroke();
            }
            ctx.fillStyle = datumRenk;
            ctx.fill();
        }

        // Datum kutusu - şeffaf veya beyaz arka plan
        const boxY = 0;
        if (!seffaf) {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(2, boxY + 2, boxSize - 4, boxSize - 4);
        }

        // Kalın siyah çerçeve - endüstri standardı (şeffaf modda beyaz outline)
        if (seffaf) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 6;
            ctx.strokeRect(3, boxY + 3, boxSize - 6, boxSize - 6);
        }
        ctx.strokeStyle = datumRenk;
        ctx.lineWidth = 3;
        ctx.strokeRect(3, boxY + 3, boxSize - 6, boxSize - 6);

        // Datum harfi (A, B, C...) - bold, ortalı (outline ile)
        ctx.font = `bold ${datumBoyut}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        drawTextWithOutline(datumLabel!, boxSize / 2, boxY + boxSize / 2);

        // Texture oluştur
        this.datum3DTexture = new THREE.CanvasTexture(canvas);
        this.datum3DTexture.minFilter = THREE.LinearFilter;
        this.datum3DTexture.magFilter = THREE.LinearFilter;

        // ═══════════════════════════════════════════════════════════════════════════
        // GERÇEK 3D MESH - PlaneGeometry (Sprite DEĞİL!)
        // ═══════════════════════════════════════════════════════════════════════════
        const aspectRatio = canvas.width / canvas.height;
        const meshWidth = 1.5 * aspectRatio;
        const meshHeight = 1.5;
        const geometry = new THREE.PlaneGeometry(meshWidth, meshHeight);

        const material = new THREE.MeshBasicMaterial({
            map: this.datum3DTexture,
            transparent: true,
            side: THREE.DoubleSide,
            depthTest: true,
            depthWrite: false
        });

        this.datum3DMesh = new THREE.Mesh(geometry, material);

        // POZİSYON: Config'den offset kullan
        const offset = datum.offset || { x: -4, y: 0, z: 0 };
        this.datum3DMesh.position.set(offset.x, offset.y, offset.z);

        // DÜZLEM ROTASYONU
        const duzlem = datum.duzlem || 'XY';
        this.applyDuzlemRotation(this.datum3DMesh, duzlem);

        this.datum3DMesh.name = 'DatumMarker3D';
        this.datum3DMesh.renderOrder = 9999;
        this.datum3DMesh.frustumCulled = false;

        this.proGroup3D.add(this.datum3DMesh);

        // Bağlantı çizgisi (datum'dan SmartPoint'e)
        const lineStartX = offset.x + 1.5; // Datum kutusunun sağ kenarından
        const lineGeom = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(lineStartX, offset.y, offset.z),
            new THREE.Vector3(0, 0, 0)
        ]);
        const lineMat = new THREE.LineBasicMaterial({
            color: new THREE.Color(datumRenk),
            linewidth: 2,
            depthTest: true,
            depthWrite: false
        });
        this.datumLine3D = new THREE.Line(lineGeom, lineMat);
        this.datumLine3D.name = 'DatumLine3D';
        this.datumLine3D.renderOrder = 9998;
        this.datumLine3D.frustumCulled = false;
        this.proGroup3D.add(this.datumLine3D);
    }

    /**
     * ═══════════════════════════════════════════════════════════════════════════
     * 3D DIMENSION LINE - Profesyonel CAD ölçü çizgisi
     * ENDÜSTRİ STANDARDI: ISO 129-1 / ASME Y14.5 formatı
     * Toleranslı boyut gösterimi: "4.489 ± .010"
     * TAM ÖZELLEŞTİRİLEBİLİR: Başlangıç/bitiş noktaları, extension uzunlukları
     * ═══════════════════════════════════════════════════════════════════════════
     */
    private createDimensionLine3D(): void {
        if (!this.proGroup3D || !this.config.dimension?.aktif) return;

        const dim = this.config.dimension;
        const dimRenk = new THREE.Color(dim.renk || '#1A1A1A');

        // Ölçü çizgisi uzunluğu - config'den veya varsayılan
        const lineLength = dim.uzunluk || 8;

        // Extension line uzunlukları - ayrı ayrı ayarlanabilir
        const extSolUzunluk = dim.extSolUzunluk ?? dim.extensionUzunluk ?? 2;
        const extSagUzunluk = dim.extSagUzunluk ?? dim.extensionUzunluk ?? 2;
        const extOffset = dim.extOffset ?? 0.3; // Çizgiden taşma

        // Başlangıç ve bitiş noktaları - özel veya hesaplanmış
        const startX = dim.baslangicNokta?.x ?? -lineLength / 2;
        const endX = dim.bitisNokta?.x ?? lineLength / 2;
        const startY = dim.baslangicNokta?.y ?? 0;
        const endY = dim.bitisNokta?.y ?? 0;

        // Dimension Group
        this.dimension3DGroup = new THREE.Group();
        this.dimension3DGroup.name = 'DimensionGroup3D';

        // Ana ölçü çizgisi
        const mainLineGeom = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(startX, startY, 0),
            new THREE.Vector3(endX, endY, 0)
        ]);
        const mainLineMat = new THREE.LineBasicMaterial({
            color: dimRenk,
            linewidth: 2,
            depthTest: true,
            depthWrite: false
        });
        this.dimensionLine3D = new THREE.Line(mainLineGeom, mainLineMat);
        this.dimensionLine3D.renderOrder = 9997;
        this.dimensionLine3D.frustumCulled = false;
        this.dimension3DGroup.add(this.dimensionLine3D);

        // Extension lines (dikey çizgiler) - ayrı ayrı uzunluk
        const extLineMat = new THREE.LineBasicMaterial({
            color: dimRenk,
            linewidth: 1,
            depthTest: true,
            depthWrite: false
        });

        // Sol extension line
        const leftExtGeom = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(startX, startY - extSolUzunluk, 0),
            new THREE.Vector3(startX, startY + extOffset, 0)
        ]);
        const leftExt = new THREE.Line(leftExtGeom, extLineMat);
        leftExt.renderOrder = 9997;
        leftExt.frustumCulled = false;
        this.dimensionExtLines3D.push(leftExt);
        this.dimension3DGroup.add(leftExt);

        // Sağ extension line
        const rightExtGeom = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(endX, endY - extSagUzunluk, 0),
            new THREE.Vector3(endX, endY + extOffset, 0)
        ]);
        const rightExt = new THREE.Line(rightExtGeom, extLineMat);
        rightExt.renderOrder = 9997;
        rightExt.frustumCulled = false;
        this.dimensionExtLines3D.push(rightExt);
        this.dimension3DGroup.add(rightExt);

        // Ok uçları - endüstri standardı
        if (dim.okTip !== 'none') {
            const arrowSize = 0.5; // Biraz daha büyük
            const arrowGeom = new THREE.ConeGeometry(arrowSize * 0.35, arrowSize, 8);
            const arrowMat = new THREE.MeshBasicMaterial({
                color: dimRenk,
                depthTest: true,
                depthWrite: false
            });

            // Sol ok (içe bakan - sağa)
            const leftArrow = new THREE.Mesh(arrowGeom.clone(), arrowMat);
            leftArrow.rotation.z = -Math.PI / 2;
            leftArrow.position.set(startX + arrowSize / 2, startY, 0);
            leftArrow.renderOrder = 9998;
            leftArrow.frustumCulled = false;
            this.dimensionArrows3D.push(leftArrow);
            this.dimension3DGroup.add(leftArrow);

            // Sağ ok (içe bakan - sola)
            const rightArrow = new THREE.Mesh(arrowGeom.clone(), arrowMat);
            rightArrow.rotation.z = Math.PI / 2;
            rightArrow.position.set(endX - arrowSize / 2, endY, 0);
            rightArrow.renderOrder = 9998;
            rightArrow.frustumCulled = false;
            this.dimensionArrows3D.push(rightArrow);
            this.dimension3DGroup.add(rightArrow);
        }

        // Ölçü metni sprite'ı
        this.createDimensionText3D(dim, Math.abs(endX - startX));

        // POZİSYON: Config'den offset kullan
        const offset = dim.offset || { x: 0, y: -3, z: 0 };
        this.dimension3DGroup.position.set(offset.x, offset.y, offset.z);

        this.proGroup3D.add(this.dimension3DGroup);
    }

    /**
     * Dimension text sprite oluştur - tolerans destekli
     * ENDÜSTRİ STANDARDI: ISO 129-1 formatı
     */
    private createDimensionText3D(dim: IDimensionConfig, _lineLength: number): void {
        if (!this.dimension3DGroup) return;

        const seffaf = dim.seffafArkaplan !== false; // Varsayılan şeffaf

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        const scale = 2;
        const width = 200;
        const height = 60;
        canvas.width = width * scale;
        canvas.height = height * scale;
        ctx.scale(scale, scale);

        // Arka plan - ŞEFFAF veya yarı saydam beyaz
        if (seffaf) {
            ctx.clearRect(0, 0, width, height);
        } else {
            ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            ctx.fillRect(0, 0, width, height);
            // Çerçeve sadece opak modda
            ctx.strokeStyle = dim.renk || '#1A1A1A';
            ctx.lineWidth = 1.5;
            ctx.strokeRect(2, 2, width - 4, height - 4);
        }

        const fontSize = dim.yaziBoyut || 14;
        const yaziRenk = dim.yaziRenk || '#1A1A1A';

        // Helper: Şeffaf modda beyaz outline ile yazı çiz
        const drawTextWithOutline = (text: string, x: number, y: number) => {
            if (seffaf) {
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 4;
                ctx.lineJoin = 'round';
                ctx.miterLimit = 2;
                ctx.strokeText(text, x, y);
            }
            ctx.fillStyle = yaziRenk;
            ctx.fillText(text, x, y);
        };

        // Tip prefix (Ø, R, vb.)
        let prefix = '';
        if (dim.tip === 'diameter') prefix = 'Ø';
        else if (dim.tip === 'radius') prefix = 'R';
        else if (dim.tip === 'angle') prefix = '∠';

        // Ana değer
        const mainValue = `${prefix}${dim.deger || '0.000'}`;

        // Tolerans var mı?
        const hasTolerance = dim.toleransUst || dim.toleransAlt;

        if (hasTolerance) {
            // Ana değer (sol taraf)
            ctx.font = `bold ${fontSize}px Arial`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            drawTextWithOutline(mainValue, 10, height / 2);

            // Tolerans (sağ taraf, üst üste)
            const tolX = 10 + ctx.measureText(mainValue).width + 5;
            ctx.font = `${fontSize * 0.7}px Arial`;

            if (dim.toleransUst) {
                ctx.textBaseline = 'bottom';
                drawTextWithOutline(dim.toleransUst, tolX, height / 2 - 2);
            }
            if (dim.toleransAlt) {
                ctx.textBaseline = 'top';
                drawTextWithOutline(dim.toleransAlt, tolX, height / 2 + 2);
            }
        } else {
            // Sadece ana değer (ortalı)
            ctx.font = `bold ${fontSize}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            drawTextWithOutline(mainValue, width / 2, height / 2);
        }

        // Birim
        if (dim.birim && dim.birim !== 'mm') {
            ctx.font = `${fontSize * 0.6}px Arial`;
            ctx.textAlign = 'right';
            drawTextWithOutline(dim.birim, width - 5, height - 8);
        }

        // Texture
        this.dimensionText3DTexture = new THREE.CanvasTexture(canvas);
        this.dimensionText3DTexture.minFilter = THREE.LinearFilter;
        this.dimensionText3DTexture.magFilter = THREE.LinearFilter;

        // ═══════════════════════════════════════════════════════════════════════════
        // GERÇEK 3D MESH - PlaneGeometry (Sprite DEĞİL!)
        // Canvas boyutu 200x60 = 10:3 aspect ratio → 4:1.2
        // ═══════════════════════════════════════════════════════════════════════════
        const meshWidth = 4;
        const meshHeight = 1.2;
        const geometry = new THREE.PlaneGeometry(meshWidth, meshHeight);

        const material = new THREE.MeshBasicMaterial({
            map: this.dimensionText3DTexture,
            transparent: true,
            side: THREE.DoubleSide,
            depthTest: true,
            depthWrite: false
        });

        this.dimensionText3DMesh = new THREE.Mesh(geometry, material);
        this.dimensionText3DMesh.position.set(0, 0.8, 0); // Çizginin üstünde
        this.dimensionText3DMesh.name = 'DimensionText3D';
        this.dimensionText3DMesh.renderOrder = 9999;

        // DÜZLEM ROTASYONU - Dimension için duzlem config'den
        const duzlem = dim.duzlem || 'XY';
        this.applyDuzlemRotation(this.dimensionText3DMesh, duzlem);

        this.dimension3DGroup.add(this.dimensionText3DMesh);
    }

    /**
     * 3D PRO Group'u sahneye ekle
     */
    private add3DProGroupToScene(): void {
        if (!this.proGroup3D || !this.sahne?.anaGosterim?.sahneler?.[0]) return;

        // PRO Group'u SmartPoint pozisyonuna taşı
        this.proGroup3D.position.copy(this.position);

        // Ana THREE.Scene'e ekle (sahneler[0] = ana sahne)
        this.sahne.anaGosterim.sahneler[0].add(this.proGroup3D);

        // İlk scale güncellemesi
        this.update3DProGroup();

        console.log('[FikoSmartPoint3D] 3D PRO Group eklendi:', {
            position: this.proGroup3D.position.toArray(),
            scale: this.proGroup3D.scale.toArray(),
            children: this.proGroup3D.children.length
        });
    }

    /**
     * 3D PRO Group'u güncelle (pozisyon ve ölçek)
     */
    private update3DProGroup(): void {
        if (!this.proGroup3D || !this.sahne?.anaGosterim?.aktifKamera) return;

        // Pozisyonu SmartPoint ile senkronize et
        this.proGroup3D.position.copy(this.position);

        const kamera = this.sahne.anaGosterim.aktifKamera as THREE.OrthographicCamera;
        const zoom = kamera?.zoom ?? 1;
        const viewHeight = ((kamera.top ?? 100) - (kamera.bottom ?? -100)) / zoom;
        const boyutCarpani = this.getBoyutCarpani();

        // Sabit boyut modu - kameraya göre ölçekle
        // viewHeight tipik olarak 100-500 arası, scale ~1-5 arası olmalı
        if (this.config.sabitBoyut) {
            // Scale faktörü - viewHeight'ın %2'si * boyut çarpanı
            const scaleFactor = viewHeight * 0.02 * boyutCarpani;
            this.proGroup3D.scale.set(scaleFactor, scaleFactor, scaleFactor);
        } else {
            // Dinamik modda sabit scale
            const scale = 10 * boyutCarpani;
            this.proGroup3D.scale.set(scale, scale, scale);
        }

        // Billboard - kameraya bak
        // Tüm PRO group kameraya bakmalı (leader line ve arrow dahil)
        this.proGroup3D.quaternion.copy(kamera.quaternion);
    }

    /**
     * 3D PRO Group'u temizle
     */
    private dispose3DProGroup(): void {
        if (!this.proGroup3D) return;

        // Sahneden kaldır
        if (this.sahne?.anaGosterim?.sahneler?.[0]) {
            this.sahne.anaGosterim.sahneler[0].remove(this.proGroup3D);
        }

        // Leader Line
        if (this.leader3DLine) {
            this.leader3DLine.geometry.dispose();
            (this.leader3DLine.material as THREE.Material).dispose();
            this.leader3DLine = null;
        }

        if (this.leader3DArrow) {
            this.leader3DArrow.geometry.dispose();
            (this.leader3DArrow.material as THREE.Material).dispose();
            this.leader3DArrow = null;
        }

        // GD&T Mesh (gerçek 3D)
        if (this.gdt3DMesh) {
            this.gdt3DMesh.geometry.dispose();
            (this.gdt3DMesh.material as THREE.Material).dispose();
            this.gdt3DMesh = null;
        }
        if (this.gdt3DTexture) {
            this.gdt3DTexture.dispose();
            this.gdt3DTexture = null;
        }

        // Yüzey Mesh (gerçek 3D)
        if (this.yuzey3DMesh) {
            this.yuzey3DMesh.geometry.dispose();
            (this.yuzey3DMesh.material as THREE.Material).dispose();
            this.yuzey3DMesh = null;
        }
        if (this.yuzey3DTexture) {
            this.yuzey3DTexture.dispose();
            this.yuzey3DTexture = null;
        }

        // Kaynak Mesh (gerçek 3D)
        if (this.kaynak3DMesh) {
            this.kaynak3DMesh.geometry.dispose();
            (this.kaynak3DMesh.material as THREE.Material).dispose();
            this.kaynak3DMesh = null;
        }
        if (this.kaynak3DTexture) {
            this.kaynak3DTexture.dispose();
            this.kaynak3DTexture = null;
        }

        // Anchor Sprite
        if (this.anchor3DSprite) {
            this.anchor3DSprite.material.dispose();
            this.anchor3DSprite = null;
        }
        if (this.anchor3DTexture) {
            this.anchor3DTexture.dispose();
            this.anchor3DTexture = null;
        }

        this.proGroup3D.clear();
        this.proGroup3D = null;
    }

    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Sağ tık menüsü - Düzenleme dialoğu aç
     */
    private onContextMenu(): void {
        // Önce callback varsa onu çağır
        if (this.config.onEdit) {
            this.config.onEdit(this);
        }

        // Ayrıca anaGosterim üzerinden event emit et (dialog kapalıysa açılması için)
        if (this.sahne?.anaGosterim?.smartPointEditRequest) {
            this.sahne.anaGosterim.smartPointEditRequest.emit(this);
        }
    }

    /**
     * Düzenleme callback'i ayarla
     */
    setOnEdit(callback: (point: FikoSmartPoint3D) => void): void {
        this.config.onEdit = callback;
    }

    /**
     * Config'i güncelle ve görünümü yenile
     */
    updateConfig(newConfig: Partial<FikoSmartPoint3DConfig>): void {
        const oldKategori = this.config.kategori;
        const needsRebuild =
            newConfig.boyut !== undefined ||
            newConfig.pencereBoyut !== undefined ||
            newConfig.animasyonAktif !== undefined ||
            newConfig.kategori !== undefined ||
            newConfig.ozelRenk !== undefined ||
            newConfig.yaziRenk !== undefined ||
            // Profesyonel CAD özellikleri değişikliği de rebuild gerektirir
            newConfig.proOnlyMod !== undefined ||
            newConfig.leader !== undefined ||
            newConfig.gdt !== undefined ||
            newConfig.yuzeyKalite !== undefined ||
            newConfig.kaynak !== undefined;

        // Config'i güncelle (deep merge for nested objects)
        if (newConfig.leader) {
            this.config.leader = { ...this.config.leader, ...newConfig.leader };
        }
        if (newConfig.gdt) {
            this.config.gdt = { ...this.config.gdt, ...newConfig.gdt };
        }
        if (newConfig.yuzeyKalite) {
            this.config.yuzeyKalite = { ...this.config.yuzeyKalite, ...newConfig.yuzeyKalite };
        }
        if (newConfig.kaynak) {
            this.config.kaynak = { ...this.config.kaynak, ...newConfig.kaynak };
        }

        // Diğer config'leri güncelle
        const { leader, gdt, yuzeyKalite, kaynak, ...restConfig } = newConfig;
        Object.assign(this.config, restConfig);

        // Kategori değiştiyse renk ve ikonu da güncelle
        if (newConfig.kategori && newConfig.kategori !== oldKategori) {
            const kategoriStil = KATEGORI_STILLERI[newConfig.kategori];
            if (!newConfig.renk) this.config.renk = kategoriStil.renk;
            if (!newConfig.ikon) this.config.ikon = kategoriStil.ikon;
        }

        // DOM'u yeniden oluştur (boyut, animasyon vb. değiştiyse)
        if (needsRebuild) {
            this.rebuildDOM();
        } else {
            // Sadece içeriği güncelle
            this.updateLabelContent();
        }

        // Scale'i güncelle
        this.guncelleScale();
    }

    /**
     * DOM elementlerini yeniden oluştur
     */
    private rebuildDOM(): void {
        const wasActive = this.isActive;

        // Eski elementleri temizle
        if (this.containerElement) {
            this.containerElement.innerHTML = '';
        }

        // PRO-only modda sadece anchor, aksi halde tam ikon
        if (this.config.proOnlyMod) {
            this.createProOnlyAnchor();
        } else {
            // Temel elementleri yeniden oluştur
            this.createIcon();
            this.createLabel();
        }

        // ═══════════════════════════════════════════════════════════════
        // 3D PRO ELEMENTLER - Yeniden oluştur
        // CSS3D yerine gerçek 3D geometry kullanılıyor
        // ═══════════════════════════════════════════════════════════════
        const has3DPro = this.config.leader?.aktif ||
                         this.config.gdt?.aktif ||
                         this.config.yuzeyKalite?.aktif ||
                         this.config.kaynak?.aktif ||
                         this.config.proOnlyMod;

        if (has3DPro && this.sahne?.anaGosterim?.sahneler?.[0]) {
            // Önce eskilerini temizle
            this.dispose3DProGroup();
            // Yenilerini oluştur
            this.create3DProGroup();
            this.add3DProGroupToScene();
        }

        // Eski durumu geri yükle (sadece normal modda)
        if (!this.config.proOnlyMod && (wasActive || this.config.etiketGorunsun)) {
            this.isActive = true;
            this.showLabel();
            if (this.iconElement) {
                this.iconElement.style.animation = 'none';
                this.iconElement.style.opacity = '0';
                this.iconElement.style.pointerEvents = 'none';
            }
        }
    }

    /**
     * Sadece label içeriğini güncelle (boyut değişmeden)
     */
    private updateLabelContent(): void {
        if (!this.labelElement) return;

        const pencereCarpani = this.getPencereBoyutCarpani();
        const domSize = Math.round(100 * pencereCarpani);
        const baseFontSize = Math.round(domSize * 0.16);
        const descFontSize = Math.round(domSize * 0.12);
        const marginBottom = this.config.aciklama ? Math.round(domSize * 0.06) : 0;

        const aktifRenk = this.getAktifRenk();
        const yaziRenk = this.getYaziRenk();

        let content = `<div style="color:${aktifRenk};font-size:${baseFontSize}px;font-weight:500;margin-bottom:${marginBottom}px">${this.config.baslik}</div>`;
        if (this.config.aciklama) {
            const descColor = yaziRenk === '#FFFFFF' ? 'rgba(255,255,255,0.65)' : yaziRenk;
            content += `<div style="color:${descColor};font-size:${descFontSize}px;font-weight:300;line-height:1.3">${this.config.aciklama}</div>`;
        }
        this.labelElement.innerHTML = content;

        // Border rengini ve yazı rengini güncelle
        const borderWidth = Math.max(2, Math.round(domSize * 0.03));
        this.labelElement.style.borderLeft = `${borderWidth}px solid ${aktifRenk}`;
        this.labelElement.style.color = yaziRenk;
    }

    /**
     * Mevcut config'i al
     */
    getConfig(): Required<FikoSmartPoint3DConfig> {
        return { ...this.config };
    }

    /**
     * Kamera konumunun kayıtlı olup olmadığını kontrol et
     */
    hasKameraKonumu(): boolean {
        return !!this.config.kameraKonumu;
    }

    /**
     * Mevcut kamera konumunu kaydet
     */
    kameraKonumuKaydet(): IKameraKonum | null {
        if (!this.sahne?.anaGosterim?.aktifKamera || !this.sahne?.anaGosterim?.aktifKontrol) {
            return null;
        }

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const kontrol = this.sahne.anaGosterim.aktifKontrol;
        const canvas = this.sahne.anaGosterim.canvas?.nativeElement;

        const konum: IKameraKonum = {
            position: kamera.position.toArray(),
            quaternion: kamera.quaternion.toArray(),
            hedef: kontrol.target.toArray(),
            ust: kamera.up.toArray(),
            yakinlik: kamera.zoom || 1,
            oran: canvas ? canvas.clientWidth / canvas.clientHeight : 1
        };

        this.config.kameraKonumu = konum;
        return konum;
    }

    /**
     * Kayıtlı kamera konumunu temizle
     */
    kameraKonumuTemizle(): void {
        this.config.kameraKonumu = undefined as any;
    }

    /**
     * Kayıtlı kamera konumuna git (animasyonlu)
     */
    async kameraKonumunaGit(sure: number = 750): Promise<boolean> {
        if (!this.config.kameraKonumu) {
            return false;
        }

        if (!this.sahne?.anaGosterim?.aktifKamera || !this.sahne?.anaGosterim?.aktifKontrol) {
            return false;
        }

        const veri = this.config.kameraKonumu;
        const aktifKamera = this.sahne.anaGosterim.aktifKamera;
        const aktifKontrol = this.sahne.anaGosterim.aktifKontrol;
        const canvas = this.sahne.anaGosterim.canvas?.nativeElement;

        return new Promise((resolve) => {
            // Sürekli render başlat
            this.sahne.anaGosterim.surekliCiz?.();

            const hedefKameraPoz = new THREE.Vector3().fromArray(veri.position);
            const hedefKameraQua = new THREE.Quaternion().fromArray(veri.quaternion);
            const hedefKontrolPoz = new THREE.Vector3().fromArray(veri.hedef);
            const hedefKameraUst = new THREE.Vector3().fromArray(veri.ust);

            // Aspect ratio'ya göre zoom ayarla
            const yeniOran = canvas ? canvas.clientWidth / canvas.clientHeight : 1;
            const yeniYakinlik = (yeniOran * veri.yakinlik) / (veri.oran || 1);

            // Başlangıç değerleri
            const baslanqicPoz = aktifKamera.position.clone();
            const baslanqicQua = aktifKamera.quaternion.clone();
            const baslanqicHedef = aktifKontrol.target.clone();
            const baslanqicUst = aktifKamera.up.clone();
            const baslanqicZoom = aktifKamera.zoom || 1;

            // Quaternion optimizasyonu
            const hedefQuaOptimize = hedefKameraQua.clone();
            if (baslanqicQua.dot(hedefQuaOptimize) < 0) {
                hedefQuaOptimize.x *= -1;
                hedefQuaOptimize.y *= -1;
                hedefQuaOptimize.z *= -1;
                hedefQuaOptimize.w *= -1;
            }

            // Ayrı progress değerleri: dönüş hızlı, zoom yavaş
            const animasyon = {
                rotProgress: 0,  // Dönüş için (hızlı)
                zoomProgress: 0  // Zoom için (yavaş)
            };

            // Dönüş animasyonu (hızlı - normal süre)
            // Promise burada resolve edilir (rotasyon bittiğinde pencere açılabilir)
            const rotTween = new TWEEN.Tween(animasyon)
                .to({ rotProgress: 1 }, sure)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onUpdate(() => {
                    const p = animasyon.rotProgress;

                    // Pozisyon lerp
                    aktifKamera.position.lerpVectors(baslanqicPoz, hedefKameraPoz, p);

                    // Quaternion slerp (dönüşler)
                    aktifKamera.quaternion.slerpQuaternions(baslanqicQua, hedefQuaOptimize, p);

                    // Up vector lerp
                    aktifKamera.up.lerpVectors(baslanqicUst, hedefKameraUst, p);

                    // Target lerp
                    aktifKontrol.target.lerpVectors(baslanqicHedef, hedefKontrolPoz, p);

                    aktifKontrol.update();
                })
                .onComplete(() => {
                    // Rotasyon bittiğinde resolve et (pencere açılabilir)
                    resolve(true);
                })
                .start();

            // Zoom animasyonu (yavaş - 2x süre, yumuşak easing)
            // Zoom arka planda devam eder, pencere rotasyon bitince açılır
            const zoomTween = new TWEEN.Tween(animasyon)
                .to({ zoomProgress: 1 }, sure * 2)
                .easing(TWEEN.Easing.Cubic.InOut)
                .onUpdate(() => {
                    const p = animasyon.zoomProgress;

                    // Zoom lerp (yavaş)
                    aktifKamera.zoom = baslanqicZoom + (yeniYakinlik - baslanqicZoom) * p;
                    aktifKamera.updateProjectionMatrix();
                })
                .onComplete(() => {
                    // Zoom bittiğinde sürekli çizimi durdur
                    this.sahne.anaGosterim.surekliCizIptal?.();
                })
                .start();
        });
    }

    /**
     * Toggle aktif durumu - CSS2D benzeri animasyonlu geçiş
     */
    toggleActive(): void {
        this.isActive = !this.isActive;

        if (this.isActive) {
            // İkonu animasyonlu gizle
            if (this.iconElement) {
                this.iconElement.style.animation = 'none'; // Pulse durdur
                this.iconElement.style.transform = 'scale(0.3)';
                this.iconElement.style.opacity = '0';
                this.iconElement.style.pointerEvents = 'none';
                this.iconElement.style.filter = 'blur(4px)';
            }
            this.showLabel();
        } else {
            this.hideLabel();
        }
    }

    /**
     * Label göster - Animasyonlu (scale + fade in)
     */
    showLabel(): void {
        if (this.labelElement) {
            // Önce görünür yap
            this.labelElement.style.display = 'block';
            this.labelElement.style.pointerEvents = 'auto';

            // Sonraki frame'de animasyonu başlat
            requestAnimationFrame(() => {
                if (this.labelElement) {
                    this.labelElement.style.opacity = '1';
                    this.labelElement.style.transform = 'translate(-50%, -50%) scale(1)';
                }
            });
        }
    }

    /**
     * Label gizle, ikon göster - Animasyonlu (scale + fade out)
     */
    hideLabel(): void {
        if (this.labelElement && !this.config.etiketGorunsun) {
            // Animasyonlu gizle
            this.labelElement.style.opacity = '0';
            this.labelElement.style.transform = 'translate(-50%, -50%) scale(0.8)';
            this.labelElement.style.pointerEvents = 'none';

            // Animasyon bitince display:none
            setTimeout(() => {
                if (this.labelElement && !this.isActive) {
                    this.labelElement.style.display = 'none';
                }
            }, 200);
        }

        // İkonu animasyonlu geri getir - bounce efekti ile
        if (this.iconElement) {
            // Önce scale(0)'dan başla
            this.iconElement.style.transform = 'scale(0)';
            this.iconElement.style.filter = 'blur(0)';

            // Sonraki frame'de animasyonu başlat
            requestAnimationFrame(() => {
                if (this.iconElement) {
                    this.iconElement.style.opacity = '1';
                    this.iconElement.style.pointerEvents = 'auto';
                    // Bounce eğrisi ile scale(1)'e dön
                    this.iconElement.style.transform = 'scale(1)';

                    // Pulse animasyonunu geri aç (küçük gecikme ile)
                    setTimeout(() => {
                        if (this.iconElement && !this.isActive) {
                            this.iconElement.style.animation = 'fikoSmartPointPulse 2s ease-in-out infinite';
                        }
                    }, 300);
                }
            });
        }
    }

    /**
     * Pozisyon ayarla
     */
    setPosition(pos: THREE.Vector3): void {
        this.position.copy(pos);
        if (this.css3dObject) {
            this.css3dObject.position.copy(pos);
        }
    }

    /**
     * Sahneye hazırla - CSS3D sahnesine ekle
     */
    hazirla(sahne: FikoSahne, css3dSahne: THREE.Scene): void {
        this.sahne = sahne;
        this.css3dSahne = css3dSahne;

        // CSS3D object'i sahneye ekle
        if (this.css3dObject) {
            css3dSahne.add(this.css3dObject);
        }

        // ═══════════════════════════════════════════════════════════════
        // 3D PRO ELEMENTLER - Ana sahneye gerçek 3D geometry olarak ekle
        // ═══════════════════════════════════════════════════════════════
        const has3DPro = this.config.leader?.aktif ||
                         this.config.gdt?.aktif ||
                         this.config.yuzeyKalite?.aktif ||
                         this.config.kaynak?.aktif ||
                         this.config.proOnlyMod;

        if (has3DPro) {
            this.create3DProGroup();
            this.add3DProGroupToScene();
        }

        // Kamera değişikliklerini dinle (her iki mod için de gerekli - billboard için)
        if (!this.listenerEklendi && sahne?.anaGosterim?.aktifKontrol) {
            sahne.anaGosterim.aktifKontrol.addEventListener('change', this.kontrolDegistiEvent);
            this.listenerEklendi = true;
        }

        // İlk boyut ve yön ayarı
        this.guncelleScale();
    }

    /**
     * Kamera değiştiğinde tetiklenir
     */
    private kameraYonDegisti(): void {
        this.guncelleScale();
    }

    /**
     * Profesyonel boyutlandırma sistemi
     *
     * SABİT BOYUT: Zoom'a TERS orantılı scale -> ekranda aynı boyut kalır
     * DİNAMİK BOYUT: Sabit scale -> zoom ile büyür/küçülür
     *
     * Boyut çarpanı hem ikon hem label için geçerlidir.
     */
    private guncelleScale(): void {
        if (!this.sahne?.anaGosterim?.aktifKamera || !this.css3dObject) return;

        const kamera = this.sahne.anaGosterim.aktifKamera as THREE.OrthographicCamera;
        const zoom = kamera?.zoom ?? 1;

        // Görünür alan yüksekliği (zoom dahil)
        const viewHeight = ((kamera.top ?? 100) - (kamera.bottom ?? -100)) / zoom;

        // Boyut çarpanı (32 = referans = 1.0x)
        const boyutCarpani = this.getBoyutCarpani();

        if (this.config.sabitBoyut) {
            // ═══════════════════════════════════════════════════════════════
            // SABİT BOYUT MODU
            // Ekranda sabit piksel boyutu - zoom değişse de aynı kalır
            // ═══════════════════════════════════════════════════════════════

            // Scale = viewHeight'a orantılı (zoom artınca viewHeight azalır, scale de azalır)
            const baseFactor = viewHeight / SIZING_CONSTANTS.FIXED.VIEW_HEIGHT_DIVISOR;
            const finalScale = Math.max(
                SIZING_CONSTANTS.FIXED.MIN_SCALE,
                Math.min(
                    SIZING_CONSTANTS.FIXED.MAX_SCALE,
                    this.originalScale.x * baseFactor * SIZING_CONSTANTS.FIXED.SCALE_FACTOR * boyutCarpani
                )
            );

            this.css3dObject.scale.set(finalScale, finalScale, finalScale);
        } else {
            // ═══════════════════════════════════════════════════════════════
            // DİNAMİK BOYUT MODU
            // 3D dünyada sabit boyut - zoom ile büyür/küçülür
            // ═══════════════════════════════════════════════════════════════
            const scale = SIZING_CONSTANTS.DYNAMIC.BASE_SCALE * boyutCarpani;
            this.css3dObject.scale.set(scale, scale, scale);
        }

        // Billboard - kameraya bak (her iki modda da)
        this.css3dObject.quaternion.copy(kamera.quaternion);

        // 3D PRO elementlerini güncelle
        this.update3DProGroup();
    }

    /**
     * Temizle
     */
    dispose(): void {
        // Listener'ı kaldır
        if (this.listenerEklendi && this.sahne?.anaGosterim?.aktifKontrol) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener('change', this.kontrolDegistiEvent);
            this.listenerEklendi = false;
        }

        // 3D PRO elementlerini temizle
        this.dispose3DProGroup();

        // CSS3D sahnesinden kaldır
        if (this.css3dSahne && this.css3dObject) {
            this.css3dSahne.remove(this.css3dObject);
        }

        // DOM elementlerini temizle
        if (this.containerElement?.parentNode) {
            this.containerElement.parentNode.removeChild(this.containerElement);
        }

        this.clear();
    }

    /**
     * JSON serialization
     */
    toJSON(): any {
        return {
            type: this.type,
            uuid: this.uuid,
            position: this.position.toArray(),
            config: this.config
        };
    }

    /**
     * JSON'dan oluştur
     */
    static fromJSON(json: any, sahne?: FikoSahne, css3dSahne?: THREE.Scene): FikoSmartPoint3D {
        const point = new FikoSmartPoint3D(json.config);
        point.uuid = json.uuid;
        point.position.fromArray(json.position);
        point.init();

        if (sahne && css3dSahne) {
            point.hazirla(sahne, css3dSahne);
        }

        return point;
    }
}

export { KATEGORI_STILLERI as KATEGORI_STILLERI_3D };
